/**
 * Zoketo OTP Login System
 * This script handles OTP-based authentication, Google SSO, and Facebook SSO integration
 * for Shopify stores. It provides a modern, secure login experience with multiple
 * authentication methods.
 */

console.log("🚀 login-override.js - Script starting to load...");

// Load Inter font from Google Fonts
(function injectInterFont() {
  console.log("📝 Injecting Inter font...");
  if (document.getElementById('hyperotp-inter-font-link')) return;
  const link = document.createElement('link');
  link.id = 'hyperotp-inter-font-link';
  link.rel = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;450;500;550;600;650;700&display=swap';
  document.head.appendChild(link);
  console.log("✅ Inter font injected");
})();

// Add a temporary CSS at the very top to immediately hide the default Shopify login form
(function injectTempHideLoginCSS() {
  console.log("🎨 Injecting temporary hide login CSS...");
  if (document.getElementById('hyperotp-temp-hide-login-style')) return;
  const style = document.createElement("style");
  style.id = "hyperotp-temp-hide-login-style";
  style.innerHTML = `
    form[action="/account/login"],
    #customer_login,
    .customer.login,
    [data-login-form],
    .shopify-section--main-password,
    .shopify-section--main-login,
    .section-header__title,
    .login-title,
    .form-title,
    .customer-login-title,
    form[action="/account/login"] *,
    #customer_login h1,
    #customer_login h2,
    .customer.login h1,
    .customer.login h2,
    .login-form,
    .login-form *,
    .container .row .col-lg-6:has(.login-form),
    .container .row .col-lg-6 .login-form,
    .container .row .col-lg-6 h2,
    .container .row .col-lg-6 form,
    .container .row .col-lg-6 .form-group,
    .container .row .col-lg-6 .btn,
    #CustomerLoginForm,
    #CustomerLoginForm *,
    .account-form,
    .account-form *,
    h1.title.row {
      display: none !important;
      visibility: hidden !important;
      height: 0 !important;
      opacity: 0 !important;
      pointer-events: none !important;
      position: absolute !important;
      z-index: -1 !important;
    }
  `;
  document.head.appendChild(style);
  console.log("✅ Temporary hide login CSS injected");
})();

console.log("✅ login-override.js loaded");

/**
 * SECTION: Initial Setup
 * Injects required scripts and sets up the base configuration
 */

// Function to check which social logins are enabled
function getEnabledSocialLogins() { 
  if (!window.hyperOTPSettings || !window.hyperOTPSettings.socialLogins || window.hyperOTPSettings.socialLogins.trim() === '') {
       return { google: false, facebook: false };
  }
  const socialLogins = window.hyperOTPSettings.socialLogins.toLowerCase().split(','); 
  const result = {
    google: socialLogins.includes('google'),
    facebook: socialLogins.includes('facebook')
  };  
  return result;
}

// Function to check if OTP form should be shown
function shouldShowOTPForm() {
  if (!window.hyperOTPSettings) return true;
  const method = window.hyperOTPSettings.loginMethod;
  return method === "ONLY_PHONE" || method === "EMAIL_AND_PHONE";
}

// Function to check if email password login should be shown
function shouldShowEmailPasswordLogin() {
  if (!window.hyperOTPSettings) return true;
  const method = window.hyperOTPSettings.loginMethod;
  return method === "ONLY_EMAIL_PASSWORD" || method === "EMAIL_AND_PHONE";
}

// Function to get country display name from phone number
function getCountryDisplayName(phone) {
  if (!phone || typeof phone !== 'string') return '';
  
  if (phone.startsWith('+971')) return 'UAE';
  if (phone.startsWith('+973')) return 'BH';
  if (phone.startsWith('+965')) return 'KW';
  if (phone.startsWith('+968')) return 'OM';
  if (phone.startsWith('+966')) return 'SA';
  if (phone.startsWith('+974')) return 'QA';
  
  return '';
}

// Function to format phone number for display
function formatPhoneDisplay(phone) {
  if (!phone || typeof phone !== 'string') return '';
  
  // Remove any non-digit characters except +
  const cleaned = phone.replace(/[^\d+]/g, '');
  
  // If it starts with +, format it nicely
  if (cleaned.startsWith('+')) {
    const countryCode = cleaned.substring(0, 4); // +971, +973, etc.
    const number = cleaned.substring(4);
    
    // Format based on country code
    if (countryCode === '+971') {
      // UAE: +971 50 123 4567
      if (number.length >= 9) {
        return `+971 ${number.substring(0, 2)} ${number.substring(2, 5)} ${number.substring(5)}`;
      }
    } else if (countryCode === '+973') {
      // Bahrain: +973 1234 5678
      if (number.length >= 8) {
        return `+973 ${number.substring(0, 4)} ${number.substring(4)}`;
      }
    } else if (countryCode === '+965') {
      // Kuwait: +965 1234 5678
      if (number.length >= 8) {
        return `+965 ${number.substring(0, 4)} ${number.substring(4)}`;
      }
    } else if (countryCode === '+968') {
      // Oman: +968 1234 5678
      if (number.length >= 8) {
        return `+968 ${number.substring(0, 4)} ${number.substring(4)}`;
      }
    } else if (countryCode === '+966') {
      // Saudi Arabia: +966 50 123 4567
      if (number.length >= 9) {
        return `+966 ${number.substring(0, 2)} ${number.substring(2, 5)} ${number.substring(5)}`;
      }
    } else if (countryCode === '+974') {
      // Qatar: +974 1234 5678
      if (number.length >= 8) {
        return `+974 ${number.substring(0, 4)} ${number.substring(4)}`;
      }
    }
    
    // Default formatting for other countries
    return cleaned;
  }
  
  return cleaned;
}

// Function to extract phone number without country code
function extractPhoneWithoutCountryCode(phone) {
  if (!phone || typeof phone !== 'string') return '';
  
  // Remove any non-digit characters except +
  const cleaned = phone.replace(/[^\d+]/g, '');
  
  // If it starts with +, remove the country code
  if (cleaned.startsWith('+')) {
    return cleaned.substring(4); // Remove the country code (+971, +973, etc.)
  }
  
  return cleaned;
}

// Function to get country code from phone number
function getCountryCodeFromPhone(phone) {
  if (!phone || typeof phone !== 'string') return '+971'; // Default to UAE
  
  // Remove any non-digit characters except +
  const cleaned = phone.replace(/[^\d+]/g, '');
  
  // If it starts with +, extract the country code
  if (cleaned.startsWith('+')) {
    const countryCode = cleaned.substring(0, 4); // +971, +973, etc.
    return countryCode;
  }
  
  return '+971'; // Default to UAE
}

// Function to generate country options based on countryCode setting
function generateCountryOptions() {
  const settings = window.hyperOTPSettings;
  const countryCode = settings?.countryCode || 'ALL';
  
  const allCountries = [
    { code: '+971', name: 'UAE' },
    { code: '+973', name: 'BH' },
    { code: '+965', name: 'KW' },
    { code: '+968', name: 'OM' },
    { code: '+966', name: 'SA' },
    { code: '+974', name: 'QA' }
  ];
  
  // If countryCode is 'ALL', return all countries
  if (countryCode === 'ALL') {
    return allCountries;
  }
  
  // If countryCode is a specific country, return only that country
  const countryMap = {
    'UAE': { code: '+971', name: 'UAE' },
    'BH': { code: '+973', name: 'BH' },
    'KW': { code: '+965', name: 'KW' },
    'OM': { code: '+968', name: 'OM' },
    'SA': { code: '+966', name: 'SA' },
    'QA': { code: '+974', name: 'QA' }
  };
  
  return countryMap[countryCode] ? [countryMap[countryCode]] : allCountries;
}

// Function to generate social login buttons HTML based on settings
function generateSocialButtonsHTML() {
  const { google, facebook } = getEnabledSocialLogins();
  if (!google && !facebook) return '';
  
  let html = '<div class="otp-social-row">';
  
  if (google) {
    html += `
      <!-- Server-side Google SSO Button -->
      <button type="button" class="otp-social-btn google-sso-btn" onclick="event.preventDefault(); var shop = (window.Shopify && window.Shopify.shop) ? window.Shopify.shop : 'yourshop.myshopify.com'; window.open('https://otp.zoketo.com/app/authgoogle?shop=' + encodeURIComponent(shop), 'googleSSO', 'width=500,height=600'); return false;">
        <img src='https://img.icons8.com/color/48/google-logo.png' alt='Google' />
      </button>
    `;
  }
  
  if (facebook) {
    html += `
      <!-- Server-side Facebook SSO Button -->
      <button type="button" class="otp-social-btn social-btn facebook-sso-btn">
        <img src='https://img.icons8.com/fluency/48/facebook-new.png' alt='Facebook' />
        <span style="font-size:0;">Facebook</span>
      </button>
    `;
  }
  
  html += '</div>';
  return html;
}

// Function to fetch settings from app proxy
async function fetchHyperOTPSettings() {
  try {
    // Check if settings are available from window object (from login-embed.liquid)
    // Only use metafield settings if they exist and are not empty
    if (window.hyperOTPUserData && 
        window.hyperOTPUserData.settings && 
        typeof window.hyperOTPUserData.settings === 'object' && 
        window.hyperOTPUserData.settings !== null &&
        Object.keys(window.hyperOTPUserData.settings).length > 0) {
      window.hyperOTPSettings = window.hyperOTPUserData.settings;
      console.log("✅ HyperOTP Settings loaded from metafield:", window.hyperOTPUserData.settings);
      return window.hyperOTPUserData.settings;
    }
    
    // Fallback to API call if window object is not available
    console.log("⚠️ Settings not found in window object, falling back to API call");
    const res = await fetch("/apps/zoketootp", {
      method: "POST",
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ step: "settings" })
    });
    const result = await res.json();
    if (result.success) {
      window.hyperOTPSettings = result.settings;
      console.log("✅ HyperOTP Settings loaded from API:", result.settings);
      return result.settings;
    } else {
      console.error("❌ Failed to load settings:", result.error);
      return null;
    }
  } catch (error) {
    console.error("❌ Failed to load settings:", error);
    return null;
  }
}

// Inject Google SSO script
(function injectGoogleSSOScript() {
  if (!document.querySelector('script[src*="accounts.google.com/gsi/client"]')) {
    const script = document.createElement('script');
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }
})();

/**
 * SECTION: Main Login UI Setup
 * Sets up the login interface and handles form visibility
 */
// Start fetching settings as soon as script loads
let hyperOTPSettingsPromise = fetchHyperOTPSettings();

// Function to detect Shopify login errors
function isShopifyLoginErrorPresent() {
  // Check for common Shopify error selectors
  const errorSelectors = [
    '.form-message', // Shopify Dawn theme
    '.errors',       // Older themes
    '.form__message', // Some other themes
  ];
  return errorSelectors.some(sel => {
    const el = document.querySelector(sel);
    return el && el.textContent && el.textContent.trim().length > 0;
  });
}

// Detect if a customer is already logged in (e.g., on /account after login)
function isCustomerLoggedIn() {
  // Common indicators: logout link or form
  return !!(
    document.querySelector('a[href="/account/logout"]') ||
    document.querySelector('form[action="/account/logout"]') ||
    document.querySelector('#customer_logout_link')
  );
}

// Function to find the best container for OTP form injection
function findBestContainer() {
  // Priority order for finding the best container
  const selectors = [
    'main',
    '#MainContent',
    '.container',
    '.login-form',
    '.row',
    '#CustomerLoginForm',
    '.account-form',
    'body'
  ];
  
  for (const selector of selectors) {
    const element = document.querySelector(selector);
    if (element) {
      // If we found .login-form, try to get its parent container
      if (selector === '.login-form') {
        return element.parentElement?.parentElement || element.parentElement || element;
      }
      // If we found #CustomerLoginForm, try to get its parent container
      if (selector === '#CustomerLoginForm') {
        return element.parentElement || element;
      }
      return element;
    }
  }
  
  return document.body;
}

// Fallback for when DOM is already loaded
if (document.readyState === 'loading') {
  console.log("📋 DOM still loading - waiting for DOMContentLoaded...");
} else {
  console.log("📋 DOM already loaded - executing initialization immediately...");
  // Execute the initialization logic immediately
  (function initializeOTP() {
    console.log("📅 DOMContentLoaded event fired - starting main initialization...");
    // Helper: only globally render on account pages
    function shouldRenderGloballyOnPage() {
      try {
        const path = window.location && window.location.pathname ? window.location.pathname : '';
        return /^\/account(\/login)?\/?$/.test(path);
      } catch (_) { return false; }
    }
    // If customer is logged in, never inject OTP UI
    if (isCustomerLoggedIn()) {
      const tempStyle = document.getElementById('hyperotp-temp-hide-login-style');
      if (tempStyle) tempStyle.remove();
      return;
    }
    
    // If Shopify login error is present, do not inject OTP UI
    if (isShopifyLoginErrorPresent()) {
      console.log("⚠️ Shopify login error detected - not injecting OTP UI");
      // Optionally, remove any temp CSS you injected
      const tempStyle = document.getElementById('hyperotp-temp-hide-login-style');
      if (tempStyle) tempStyle.remove();
      return;
    }
    // 1. Render the OTP form immediately with loading state (only on account pages)
    if (shouldRenderGloballyOnPage()) {
      if (!document.getElementById('otp-login-container')) {
        const otpContainer = document.createElement("div");
        otpContainer.id = "otp-login-container";
        otpContainer.className = "otp-fade-in";
        otpContainer.style.cssText = `
          background: #fff;
          max-width: 470px;
          margin: 40px auto;
          border-radius: 16px;
          box-shadow: 0 2px 12px rgba(0,0,0,0.07);
          padding: 12px 24px 24px 24px;
          font-family: 'Inter', sans-serif;
          opacity: 0;
          transition: opacity 0.33s cubic-bezier(.4,0,.2,1);
         
        `;
        renderInitialForm(otpContainer, { loadingSocial: true });
        const container = findBestContainer();
        container.appendChild(otpContainer);
        setTimeout(() => { otpContainer.style.opacity = '1'; }, 10);
      }
    }

    // Remove loader if present
    const loader = document.getElementById('otp-loader');
    if (loader) loader.remove();

    // When settings are ready, update the UI
    hyperOTPSettingsPromise.then(settings => {
      window.hyperOTPSettings = settings;
      const tempStyle = document.getElementById('hyperotp-temp-hide-login-style');
      const otpContainer = document.getElementById('otp-login-container');
      if (!settings || settings.appEnabled === false) {
        // Remove the temp CSS so default login shows
        if (tempStyle) tempStyle.remove();
        if (otpContainer) otpContainer.remove();
        return;
      }
      if (settings.loginMethod === 'ONLY_EMAIL_PASSWORD') {
        if (tempStyle) tempStyle.remove();
        showShopifyDefaultLoginForm();
        return;
      }
      // Apply dynamic button colors based on settings
      applyDynamicButtonColors();
      // Now update the OTP UI with real settings
      if (otpContainer) {
        renderInitialForm(otpContainer, { loadingSocial: false });
        const { google, facebook } = getEnabledSocialLogins();
        const showOTPForm = shouldShowOTPForm();
        const showEmailPassword = shouldShowEmailPasswordLogin();
        const googleButton = otpContainer.querySelector('.google-sso-btn');
        const facebookButton = otpContainer.querySelector('button:has(img[alt="Facebook"])');
        const emailButton = otpContainer.querySelector('.email-btn');
        const divider = otpContainer.querySelector('.divider');
        const otpFormElement = otpContainer.querySelector('#otp-form');
        if (googleButton) {
          if (google) {
            googleButton.disabled = false;
            googleButton.style.opacity = '1';
          } else {
            googleButton.style.display = 'none';
          }
        }
        if (facebookButton) {
          if (facebook) {
            facebookButton.disabled = false;
            facebookButton.style.opacity = '1';
          } else {
            facebookButton.style.display = 'none';
          }
        }
        if (emailButton) {
          if (showEmailPassword) {
            emailButton.disabled = false;
            emailButton.style.opacity = '1';
          } else {
            emailButton.style.display = 'none';
          }
        }
        if (otpFormElement) {
          otpFormElement.style.display = showOTPForm ? 'block' : 'none';
        }
        if (divider && !google && !facebook && !showEmailPassword) {
          divider.style.display = 'none';
        }
        attachGoogleSSOHandlers();
        attachFacebookSSOHandlers();
      }
      injectHideLoginCSS();
      if (tempStyle) tempStyle.remove();
    });
  })();
}

document.addEventListener("DOMContentLoaded", () => {
  console.log("📅 DOMContentLoaded event fired - starting main initialization...");
  // Helper: only globally render on account pages
  function shouldRenderGloballyOnPage() {
    try {
      const path = window.location && window.location.pathname ? window.location.pathname : '';
      return /^\/account(\/login)?\/?$/.test(path);
    } catch (_) { return false; }
  }
  // If customer is logged in, never inject OTP UI
  if (isCustomerLoggedIn()) {
    const tempStyle = document.getElementById('hyperotp-temp-hide-login-style');
    if (tempStyle) tempStyle.remove();
    const otpContainer = document.getElementById('otp-login-container');
    if (otpContainer) otpContainer.remove();
    return;
  }
  
  // If Shopify login error is present, do not inject OTP UI
  if (isShopifyLoginErrorPresent()) {
    console.log("⚠️ Shopify login error detected - not injecting OTP UI");
    // Optionally, remove any temp CSS you injected
    const tempStyle = document.getElementById('hyperotp-temp-hide-login-style');
    if (tempStyle) tempStyle.remove();
    return;
  }
  // 1. Render the OTP form immediately with loading state (only on account pages)
  if (shouldRenderGloballyOnPage()) {
    if (!document.getElementById('otp-login-container')) {
      const otpContainer = document.createElement("div");
      otpContainer.id = "otp-login-container";
      otpContainer.className = "otp-fade-in";
      otpContainer.style.cssText = `
        background: #fff;
        max-width: 470px;
        margin: 40px auto;
        border-radius: 16px;
        box-shadow: 0 2px 12px rgba(0,0,0,0.07);
        padding: 12px 24px 24px 24px;
        font-family: 'Inter', sans-serif;
        opacity: 0;
        transition: opacity 0.33s cubic-bezier(.4,0,.2,1);
       
      `;
      renderInitialForm(otpContainer, { loadingSocial: true });
      const container = findBestContainer();
      container.appendChild(otpContainer);
      setTimeout(() => { otpContainer.style.opacity = '1'; }, 10);
    }
  }

  // Remove loader if present
  const loader = document.getElementById('otp-loader');
  if (loader) loader.remove();

  // When settings are ready, update the UI
  hyperOTPSettingsPromise.then(settings => {
    window.hyperOTPSettings = settings;
    const tempStyle = document.getElementById('hyperotp-temp-hide-login-style');
    const otpContainer = document.getElementById('otp-login-container');
    // If customer logged in by the time settings arrive, clean up and exit
    if (isCustomerLoggedIn()) {
      if (tempStyle) tempStyle.remove();
      if (otpContainer) otpContainer.remove();
      return;
    }
    // If customer logged in by the time settings arrive, clean up and exit
    if (isCustomerLoggedIn()) {
      if (tempStyle) tempStyle.remove();
      if (otpContainer) otpContainer.remove();
      return;
    }
    if (!settings || settings.appEnabled === false) {
      // Remove the temp CSS so default login shows
      if (tempStyle) tempStyle.remove();
      if (otpContainer) otpContainer.remove();
      return;
    }
    if (settings.loginMethod === 'ONLY_EMAIL_PASSWORD') {
      if (tempStyle) tempStyle.remove();
      showShopifyDefaultLoginForm();
      return;
    }
    // Apply dynamic button colors based on settings
    applyDynamicButtonColors();
    // Now update the OTP UI with real settings
    if (otpContainer) {
      renderInitialForm(otpContainer, { loadingSocial: false });
      const { google, facebook } = getEnabledSocialLogins();
      const showOTPForm = shouldShowOTPForm();
      const showEmailPassword = shouldShowEmailPasswordLogin();
      const googleButton = otpContainer.querySelector('.google-sso-btn');
      const facebookButton = otpContainer.querySelector('button:has(img[alt="Facebook"])');
      const emailButton = otpContainer.querySelector('.email-btn');
      const divider = otpContainer.querySelector('.divider');
      const otpFormElement = otpContainer.querySelector('#otp-form');
      if (googleButton) {
        if (google) {
          googleButton.disabled = false;
          googleButton.style.opacity = '1';
        } else {
          googleButton.style.display = 'none';
        }
      }
      if (facebookButton) {
        if (facebook) {
          facebookButton.disabled = false;
          facebookButton.style.opacity = '1';
        } else {
          facebookButton.style.display = 'none';
        }
      }
      if (emailButton) {
        if (showEmailPassword) {
          emailButton.disabled = false;
          emailButton.style.opacity = '1';
        } else {
          emailButton.style.display = 'none';
        }
      }
      if (otpFormElement) {
        otpFormElement.style.display = showOTPForm ? 'block' : 'none';
      }
      if (divider && !google && !facebook && !showEmailPassword) {
        divider.style.display = 'none';
      }
      attachGoogleSSOHandlers();
      attachFacebookSSOHandlers();
    }
    injectHideLoginCSS();
    if (tempStyle) tempStyle.remove();
  });
});

/**
 * SECTION: OTP Form Handler
 * Manages the OTP form submission, verification, and user registration process
 * @param {HTMLFormElement} form - The OTP form element
 * @param {HTMLElement} otpContainer - The container element for the OTP UI
 */
function attachOtpFormHandler(form, otpContainer) {
  const stepInput = form.querySelector('input[name="step"]');
  const identifierInput = form.querySelector('input[name="identifier"]');
  const countryCodeSelect = form.querySelector('#country-code');
  const otpInputWrapper = form.querySelector("#otp-input-wrapper");
  const otpInput = form.querySelector('input[name="otp"]');
  const nameInputWrapper = form.querySelector("#name-input-wrapper");
  const emailInputWrapper = form.querySelector("#email-input-wrapper");
  const firstNameInput = form.querySelector('input[name="first_name"]');
  const lastNameInput = form.querySelector('input[name="last_name"]');
  const emailInput = form.querySelector('input[name="email"]');
  const messageBox = document.getElementById("otp-message");
  const submitButton = form.querySelector("button[type='submit']");

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    
    // Fetch settings if not already loaded
    if (!window.hyperOTPSettings) {
      await fetchHyperOTPSettings();
    }
    
    const step = stepInput ? stepInput.value : "send";
    let identifier = identifierInput ? identifierInput.value.trim() : "";
    let countryCode = countryCodeSelect ? countryCodeSelect.value : "";
    // Remove all spaces from identifier before any further processing (for phone only)
    if (identifier && !identifier.includes("@")) {
      identifier = identifier.replace(/\s+/g, "");
    }
    // Only concatenate if not email and not already has country code
    if (identifier && countryCode && !identifier.startsWith("+") && !identifier.includes("@")) {
      identifier = countryCode + identifier;
    }
    const otp = otpInput?.value?.trim();

    // Phone validation for GCC countries
    function isValidGCCPhone(phone) {
      if (typeof phone !== 'string') return false;
      
      // UAE: +971XXXXXXXXX (9 digits after +971)
      if (phone.startsWith('+971')) {
        const number = phone.slice(4);
        return /^\d{9}$/.test(number);
      }
      
      // Bahrain: +973XXXXXXXX (8 digits after +973)
      if (phone.startsWith('+973')) {
        const number = phone.slice(4);
        return /^\d{8}$/.test(number);
      }
      
      // Kuwait: +965XXXXXXXX (8 digits after +965)
      if (phone.startsWith('+965')) {
        const number = phone.slice(4);
        return /^\d{8}$/.test(number);
      }
      
      // Oman: +968XXXXXXXX (8 digits after +968)
      if (phone.startsWith('+968')) {
        const number = phone.slice(4);
        return /^\d{8}$/.test(number);
      }
      
      // Saudi Arabia: +966XXXXXXXX (8 digits after +966)
      if (phone.startsWith('+966')) {
        const number = phone.slice(4);
        return /^\d{9}$/.test(number);
      }
      
      // Qatar: +974XXXXXXXX (8 digits after +974)
      if (phone.startsWith('+974')) {
        const number = phone.slice(4);
        return /^\d{8}$/.test(number);
      }
      
      return true; // For other countries, don't validate here
    }

    if (!identifier) {
      if (messageBox) {
        messageBox.style.color = "red";
        messageBox.innerText = "Please enter a phone number.";
      }
      return;
    }
    // GCC phone validation (only for phone, not email)
    if (!identifier.includes("@") && !isValidGCCPhone(identifier)) {
      if (messageBox) {
        const countryCode = identifier.startsWith('+') ? identifier.substring(0, 4) : '';
        let errorMessage = "Please enter a valid mobile number.";
        
        if (countryCode === "+971") {
          errorMessage = "Please enter a valid UAE mobile number (9 digits, digits only).";
        } else if (countryCode === "+973") {
          errorMessage = "Please enter a valid Bahrain mobile number (8 digits, digits only).";
        } else if (countryCode === "+965") {
          errorMessage = "Please enter a valid Kuwait mobile number (8 digits, digits only).";
        } else if (countryCode === "+968") {
          errorMessage = "Please enter a valid Oman mobile number (8 digits, digits only).";
        } else if (countryCode === "+966") {
          errorMessage = "Please enter a valid Saudi Arabia mobile number (9 digits, digits only).";
        } else if (countryCode === "+974") {
          errorMessage = "Please enter a valid Qatar mobile number (8 digits, digits only).";
        }
        
        messageBox.style.color = "red";
        messageBox.innerText = errorMessage;
      }
      return;
    }

    if (step === "send") {
      submitButton.disabled = true;
      submitButton.innerHTML = '<span class="otp-spinner"></span>';
      const res = await fetch("/apps/zoketootp", {
        method: "POST",
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: identifier, step: "send" })
      });
      const result = await res.json();
     // console.log("[OTP]", result.otp);
     // console.log("[SEND OTP RESPONSE]", result);
      submitButton.disabled = false;

      if (result.success) {
        if (messageBox) {
          messageBox.innerText = result.message || "OTP sent successfully.";
          messageBox.style.color = "green";
        }
        // Get enabled social logins and check if OTP form should be shown
        const { google, facebook } = getEnabledSocialLogins();
        const showSocialDivider = google || facebook;
        const showOTPForm = shouldShowOTPForm();
        const showEmailPassword = shouldShowEmailPasswordLogin();
        
        if (!showOTPForm) {
          // If OTP form should not be shown, just show social buttons and email password if enabled
          otpContainer.innerHTML = `
            <style>
              // ... existing styles ...
            </style>
            <h2 class="otp-title" style="color: ${window.hyperOTPSettings?.loginHeaderColor || 'white'}; font-size: 22px;">${window.hyperOTPSettings?.loginHeader || 'Login/Signup'}</h2>
            <div id="otp-message" style="margin-bottom: 1rem; color: green;"></div>
            ${generateSocialButtonsHTML()}
            ${showEmailPassword ? `<button type="button" class="otp-email-btn">Login with Email & Password</button>` : ''}
            <div style="font-size: 10px; text-align: right; padding-top: 8px;">Powered by Zoketo</div>
          `;
          return;
        }
        
        // Custom OTP UI for verify step
        const showEmailPassword2 = shouldShowEmailPasswordLogin();
        otpContainer.innerHTML = `
            <style>
              /* Force LTR direction to prevent RTL language switching issues */
              #otp-login-container,
              #otp-login-container * {
                direction: ltr !important;
                text-align: left !important;
                unicode-bidi: normal !important;
              }
              
              .otp-verify-title { 
                font-family: Inter, sans-serif; 
                font-size: 30px; 
                font-weight: 700; 
                color: #000; 
                text-align: center; 
                margin-top: 0; 
                margin-bottom: 15px; 
                direction: ltr !important;
                unicode-bidi: normal !important;
              }
              .otp-verify-desc { 
                 text-align: left; 
                color: #000000; 
                margin-bottom: -11px; 
                font-size: 13px; 
                font-family: "Inter", sans-serif;
                font-optical-sizing: auto;
                font-weight: 215;
                font-style: normal;
                direction: ltr !important;
                unicode-bidi: normal !important;
              }
              .otp-verify-row { display: flex; align-items: center; justify-content: flex-start; gap: 8px; margin-bottom: 1.2rem; }
              .otp-verify-in { 
               text-align: left; color: #000; margin-bottom: 0.8rem; font-weight: 700; font-size: 16px; font-family: Inter, sans-serif;  }
              .otp-verify-change { font-family: Inter, sans-serif; font-size: 18px;  font-weight: 450; color: #005BD3; text-decoration: underline; cursor: pointer;  }
              .otp-verify-form-row { display: flex; flex-direction: row; align-items: left; gap: 4px; width: 100%; margin-bottom: 0.8rem; justify-content: center; }
              .otp-verify-input { 
                flex: 1; 
                height: 45px; 
                margin-top: 0; 
                padding: 0 0px; 
                border: 0.66px solid #8A8A8A; 
                background: #FDFDFD; 
                font-size: 30px; 
                color: #303030; 
                font-family: Inter, sans-serif; 
                font-variant-numeric: tabular-nums; 
                border-radius: 8px; 
                outline: none; 
                text-align: center !important; 
                text-indent: 0 !important;
                padding-left: 0 !important;
                padding-right: 0 !important;
                box-shadow: none !important; 
                letter-spacing: 0.9em; /* visual spacing between digits */
                direction: ltr !important;
                unicode-bidi: normal !important;
              }
              /* Strong specificity to defeat theme overrides */
              #otp-login-container input.otp-verify-input {
                letter-spacing: 0.9em !important;
                text-align: center !important;
                direction: ltr !important;
              }
              
              /* Ensure RTL overrides cannot flip alignment */
              [dir="rtl"] .otp-verify-input,
              html[dir="rtl"] .otp-verify-input {
                text-align: center !important;
                direction: ltr !important;
              }
              /* Absolute last-resort override for heavily opinionated themes */
              input.otp-verify-input[style] {
                text-align: center !important;
                direction: ltr !important;
              }
              /* Placeholder centering with #otp-login-container scope for higher specificity */
              #otp-login-container .otp-verify-input::placeholder { 
                letter-spacing: 0; 
                font-size: 16px; 
                text-align: center !important; 
              }
              #otp-login-container .otp-verify-input::-webkit-input-placeholder { 
                text-align: center !important; 
              }
              #otp-login-container .otp-verify-input::-moz-placeholder { 
                text-align: center !important; 
              }
              #otp-login-container .otp-verify-input:-ms-input-placeholder { 
                text-align: center !important; 
              }
              #otp-login-container .otp-verify-input::-ms-input-placeholder { 
                text-align: center !important; 
              }
              .otp-verify-input:focus { 
                background: #fff; 
                border: 0.66px solid #8A8A8A; 
                box-shadow: none !important; 
                outline: none !important; 
                text-align: center !important;
                direction: ltr !important;
                unicode-bidi: normal !important;
              }
              .email-input { 
                font-size: 16px; 
                text-align: left; 
                letter-spacing: normal; 
                padding: 0 12px; 
                direction: ltr !important;
                unicode-bidi: normal !important;
              }
              .email-input::placeholder { 
                font-size: 14px; 
                letter-spacing: normal; 
                direction: ltr !important;
                text-align: left !important;
                unicode-bidi: normal !important;
              }
              .otp-verify-btn {
              width: 100%;
      background: #ffff;
      color: #fff;
      border: none;
      border-radius: 8px;
      height: 48px;
      font-size: 18px;
      margin-bottom: 10px;
      cursor: pointer;
      box-shadow: #ffff;
      transition: background 0.2s;
               }
              .otp-verify-btn:hover { background: #303030; }
              .otp-verify-bottom-row { display: flex; align-items: left; gap: 8px;  }
              .otp-verify-bottom-label { font-family: Inter, sans-serif; padding-top: 13px; font-size: 13px; font-weight: 650; color: #000; letter-spacing: 0px; line-height: 20px; }
              .otp-verify-change-label {
                text-align: left;
                color: #222;
                margin-bottom: 12px;
                font-size: 15px;
                font-family: 'Inter', Inter, sans-serif;
                font-weight: 650;
                letter-spacing: 0;
                line-height: 20px;
                padding: 6px 0;
              }
              .otp-verify-bottom-link { font-family: Inter, sans-serif; padding-top: 10px; font-size: 14px; font-weight: 650; color: #00527C; text-decoration: underline; cursor: pointer; }
              .otp-divider { display: flex; align-items: center; gap: 16px; width: 100%; margin: 5px 2px 7px 0; }
              .otp-divider-hr { flex: 1; border: none; border-top: 1px solid #8A8A8A; height: 1px; background: none; margin: 0; }
              .otp-divider-text { color: #000; font-family: 'SF Pro Text', Inter, sans-serif; font-size: 12px; font-weight: 600; }
              .otp-social-row { display: flex; gap: 8px; width: 100%; margin: -4px 0 8px 0; }
              .otp-social-btn { flex: 1; display: flex; align-items: center; justify-content: center; background: #fff; border: 1px solid #E3E3E3; border-radius: 8px; height: 52px; cursor: pointer; transition: border 0.2s; }
              .otp-social-btn:focus { outline: none; border: 1.5px solid #8A8A8A; }
              .otp-social-btn img { width: 28px; height: 28px; display: block; }
              .otp-social-btn span { margin-left: 8px; font-size: 16px; color: #303030; font-family: Inter, sans-serif; }
              .otp-email-btn {
                        width: 100%;       
        background: #fff;
        border: 1px solid #ddd;
        border-radius: 8px;
        line-height: 45px; 
        font-size: 18px;
        font-family: Inter, sans-serif;
        font-weight:500;
        cursor: pointer;
        color: #303030;
        transition: background 0.2s, border 0.2s; 
                }
              .otp-email-btn:focus { outline: none; border: 1.5px solid #8A8A8A; }
              .otp-spinner {
                display: inline-block;
                width: 22px;
                height: 22px;
                border: 3px solid #fff;
                border-radius: 50%;
                border-top: 3px solid #1A1A1A;
                animation: otp-spin 0.7s linear infinite;
                vertical-align: middle;
                margin-right: 8px;
              }
              @keyframes otp-spin {
                0% { transform: rotate(0deg);}
                100% { transform: rotate(360deg);}
              }
            </style>
            <h2 class="otp-verify-title">Enter OTP</h2>
            <div id="otp-message" style="margin-bottom: 1rem; color: red;"></div>
            <div class="otp-verify-desc"><b>OTP sent successfully on Phone, please enter it below</b></div>
            
             <div class="otp-verify-bottom-row">
                <p class="otp-verify-change-label">${getCountryDisplayName(identifier)} ${formatPhoneDisplay(identifier)}  <a href="#" id="otp-change-link" class="otp-verify-bottom-link">Change</a></p>
               
            </div>
            <form id="otp-form">
              <input type="hidden" name="step" value="verify" />
              <input type="hidden" name="identifier" value="${identifier}" />
              <div id="otp-input-wrapper" class="otp-verify-form-row">
                <input name="otp" placeholder="Enter OTP" class="otp-verify-input" dir="ltr" style="text-align:center !important;" />
              </div>
              <div id="name-input-wrapper" class="otp-verify-form-row" style="display:none;">
                <input name="first_name" placeholder="First Name" class="otp-verify-input" style="flex:1;" />
                <input name="last_name" placeholder="Last Name" class="otp-verify-input" style="flex:1;" />
              </div>
              <div id="email-input-wrapper" class="otp-verify-form-row" style="display:none;">
                <input name="email" placeholder="Enter your email address" class="otp-verify-input email-input" style="flex:1;" />
              </div>
              <button type="submit" class="otp-verify-btn">Submit OTP</button>
              <div class="otp-verify-bottom-row">
                <span class="otp-verify-bottom-label">OTP not Received?</span>
                <a href="#" id="otp-resend-link" class="otp-verify-bottom-link">Resend OTP</a>
              </div>
            </div>
          </form>
            ${showSocialDivider ? `
              <div class="otp-divider">
                <hr class="otp-divider-hr" />
                <span class="otp-divider-text">OR</span>
                <hr class="otp-divider-hr" />
              </div>
              ${generateSocialButtonsHTML()}
            ` : ''}
            ${showEmailPassword2 ? `<button type="button" class="otp-email-btn">Login with Email & Password</button>` : ''}
            <div style="font-size: 10px; text-align: right; padding-top: 8px;">Powered by Zoketo</div>
        `;
        // Apply dynamic button colors
        applyDynamicButtonColors();
        // Re-attach main handler to new OTP form
        const newForm = otpContainer.querySelector('#otp-form');
        attachOtpFormHandler(newForm, otpContainer);
        // Add OTP input restrictions: only 4 digits, numeric only
        if (newForm.querySelector('input[name="otp"]')) {
          const otpInput = newForm.querySelector('input[name="otp"]');
          otpInput.setAttribute('maxlength', '4');
          otpInput.setAttribute('inputmode', 'numeric');
          otpInput.setAttribute('pattern', '\\d{4}');
          otpInput.addEventListener('input', function() {
            // digits only, max 4
            let digits = otpInput.value.replace(/[^0-9]/g, '').slice(0, 4);
            otpInput.value = digits;
          });
          otpInput.addEventListener('paste', function(e) {
            e.preventDefault();
            const text = (e.clipboardData || window.clipboardData).getData('text');
            let digits = String(text || '').replace(/[^0-9]/g, '').slice(0, 4);
            otpInput.value = digits;
          });
        }
        // Change and Resend OTP links
        otpContainer.querySelector('#otp-change-link').addEventListener('click', (e) => {
          e.preventDefault();
          // Get the identifier (phone) from the hidden input
          const identifierValue = newForm.querySelector('input[name="identifier"]').value;
          // Extract phone number without country code and get the country code
          const phoneWithoutCountryCode = extractPhoneWithoutCountryCode(identifierValue);
          const countryCode = getCountryCodeFromPhone(identifierValue);
          
          // Re-render the Get OTP form with the phone pre-filled and loading state
          renderInitialForm(otpContainer, { loadingSocial: true, prefillPhone: phoneWithoutCountryCode, selectedCountryCode: countryCode });
          fetchHyperOTPSettings().then(() => {
            renderInitialForm(otpContainer, { loadingSocial: false, prefillPhone: phoneWithoutCountryCode, selectedCountryCode: countryCode });
            const { google, facebook } = getEnabledSocialLogins();
            const showOTPForm = shouldShowOTPForm();
            const showEmailPassword = shouldShowEmailPasswordLogin();
            const googleButton = otpContainer.querySelector('.google-sso-btn');
            const facebookButton = otpContainer.querySelector('button:has(img[alt="Facebook"])');
            const emailButton = otpContainer.querySelector('.email-btn');
            const divider = otpContainer.querySelector('.divider');
            const otpFormElement = otpContainer.querySelector('#otp-form');
            if (googleButton) {
              if (google) {
                googleButton.disabled = false;
                googleButton.style.opacity = '1';
              } else {
                googleButton.style.display = 'none';
              }
            }
            if (facebookButton) {
              if (facebook) {
                facebookButton.disabled = false;
                facebookButton.style.opacity = '1';
              } else {
                facebookButton.style.display = 'none';
              }
            }
            if (emailButton) {
              if (showEmailPassword) {
                emailButton.disabled = false;
                emailButton.style.opacity = '1';
              } else {
                emailButton.style.display = 'none';
              }
            }
            if (otpFormElement) {
              otpFormElement.style.display = showOTPForm ? 'block' : 'none';
            }
            if (divider && !google && !facebook && !showEmailPassword) {
              divider.style.display = 'none';
            }
            attachGoogleSSOHandlers();
            attachFacebookSSOHandlers();
          });
        });
        otpContainer.querySelector('#otp-resend-link').addEventListener('click', async (e) => {
          e.preventDefault();
          const resendLink = otpContainer.querySelector('#otp-resend-link');
          if (resendLink) {
            resendLink.innerHTML = '<span class="otp-spinner" style="display:inline-block;vertical-align:middle;min-width:22px;min-height:22px;"></span>';
            resendLink.style.pointerEvents = 'none';
          }
          // Get the identifier (phone) from the hidden input
          const identifierValue = newForm.querySelector('input[name="identifier"]').value;
          // Directly call the backend to resend OTP
          const messageBox = otpContainer.querySelector('#otp-message');
          try {
            const res = await fetch('/apps/zoketootp', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ phone: identifierValue, step: 'send' })
            });
            const result = await res.json();
            if (result.success) {
              if (messageBox) {
                messageBox.style.color = 'green';
                messageBox.innerText = result.message || 'OTP resent successfully.';
              }
            } else {
              if (messageBox) {
                messageBox.style.color = 'red';
                messageBox.innerText = result.error || 'Failed to resend OTP.';
              }
            }
          } catch (err) {
            if (messageBox) {
              messageBox.style.color = 'red';
              messageBox.innerText = 'Failed to resend OTP.';
            }
          } finally {
            if (resendLink) {
              resendLink.innerHTML = 'Resend OTP';
              resendLink.style.pointerEvents = '';
            }
          }
        });
        return;
      } else {
          if (messageBox) {
        messageBox.innerText = result.error || "Failed to send OTP.";
        messageBox.style.color = "red";
          }
        submitButton.innerHTML = "Send OTP";
      }
    } else if (step === "verify" || step === "complete") {
      // Validate OTP input
      if (!otp || otp.length !== 4 || !/^[0-9]{4}$/.test(otp)) {
        if (messageBox) {
          messageBox.style.color = "red";
          messageBox.innerText = "Please enter a valid 4-digit OTP.";
        }
        return;
      }
      // If step is complete, validate email
      if (step === "complete") {
        if (!emailInput || !emailInput.value.trim()) {
          if (messageBox) {
            messageBox.style.color = "red";
            messageBox.innerText = "Please enter your email.";
          }
          return;
        }
      }
      submitButton.disabled = true;
      submitButton.innerHTML = '<span class="otp-spinner"></span>';
      // Prepare payload
      // Strip spaces from visually formatted OTP
      const otpDigits = (otp || '').replace(/\s+/g, '');
      let payload = { phone: identifier, otp: otpDigits, step };
      if (step === "complete") payload.email = emailInput.value.trim();
      const res = await fetch("/apps/zoketootp", {
        method: "POST",
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const result = await res.json();
     // console.log("[OTP VERIFY/COMPLETE RESPONSE]", result);
      submitButton.disabled = false;

      // Handle requireEmail response from backend
      if (result.requireEmail) {
        // Hide OTP input, show email input
        if (emailInputWrapper) emailInputWrapper.style.display = '';
        if (otpInputWrapper) otpInputWrapper.style.display = 'none';
        if (submitButton) submitButton.innerText = 'Continue';
        if (stepInput) stepInput.value = 'update_email';
        form.dataset.customerId = result.customerId;
        if (messageBox) {
          messageBox.style.color = 'red';
          messageBox.innerText = result.message || 'Please enter your email to continue.';
        }
        return;
      }

      // NEW: If new customer, show signup form (name/email)
      if (result.success && result.existingCustomer === false) {
        // Render a separate signup form for new customers
        otpContainer.style.background = '#fff';
        otpContainer.style.borderRadius = '16px';
        otpContainer.style.border = '1px solid #E3E3E3';
        otpContainer.style.boxShadow = '0px 3px 1px -1px rgba(26,26,26,0.07), inset 0px 1px 0px 0px rgba(204,204,204,0.5), inset 0px -1px 0px 0px rgba(0,0,0,0.17), inset -1px 0px 0px 0px rgba(0,0,0,0.13), inset 1px 0px 0px 0px rgba(0,0,0,0.13)';
        otpContainer.style.maxWidth = '484px';
        otpContainer.style.margin = '40px auto';
        otpContainer.style.padding = '40px 32px 32px 32px';
        otpContainer.style.display = 'flex';
        otpContainer.style.flexDirection = 'column';
        otpContainer.style.gap = '0';
        // Check registerFields setting
        const registerFields = (window.hyperOTPSettings && window.hyperOTPSettings.registerFields) ? window.hyperOTPSettings.registerFields : '';
        // Check acceptEnabled setting
        const acceptEnabled = (window.hyperOTPSettings && window.hyperOTPSettings.acceptEnabled) ? window.hyperOTPSettings.acceptEnabled : false;
        otpContainer.innerHTML = `
          <style>
            /* Force LTR direction to prevent RTL language switching issues */
            #otp-login-container,
            #otp-login-container * {
              direction: ltr !important;
              text-align: left !important;
              unicode-bidi: normal !important;
            }
            
            .signup-title {
              font-family: Inter, sans-serif;
              font-size: 30px;
              font-weight: 700;
              color: #000;
              margin-bottom: 0.5rem;
              text-align: left;
              letter-spacing: -0.3px;
              line-height: 40px;
              direction: ltr !important;
              unicode-bidi: normal !important;
            }
            .signup-desc {
              color: #616161;
              font-size: 14px;
              font-family: Inter, sans-serif;
              font-weight: 650;
              margin-bottom: 1.5rem;
              text-align: left;
              line-height: 1.43;
            }
            .signup-form-row {
              display: flex;
              flex-direction: column;
              gap: 4px;
              margin-bottom: 1.2rem;
            }
            .signup-label {
              font-family: Inter, sans-serif;
              font-size: 13px;
              font-weight: 450;
              color: #303030;
              margin-bottom: 2px;
              text-align: left;
            }
            .signup-input {
              height: 45px;
              padding: 0 16px;
              border: 1px solid #8A8A8A;
              background: #FDFDFD;
              font-size: 18px;
              color: #303030;
              font-family: Inter, sans-serif;
              border-radius: 8px;
              outline: none;
              transition: border 0.2s;
              box-shadow: none !important;
              direction: ltr !important;
              text-align: left !important;
              unicode-bidi: normal !important;
            }
            .signup-input:focus {
              border: 1px solid #8A8A8A;
              background: #fff;
              box-shadow: none !important;
              outline: none !important;
            }
            .signup-input[readonly] {
              background: #F5F5F5;
              color: #8A8A8A;
            }
            .signup-input::placeholder {
              color: #8A8A8A;
              font-size: 18px;
              font-family: Inter, sans-serif;
              opacity: 1;
              direction: ltr !important;
              text-align: left !important;
              unicode-bidi: normal !important;
            }
            .signup-btn {
              background: #1A1A1A;
              color: #fff;
              border: none;
              font-size: 18px;
              font-family: Inter, sans-serif;
              font-weight: 550;
              height: 52px;
              width: 100%;
              margin-top: 8px;
              cursor: pointer;
              border-radius: 8px;
              transition: background 0.2s;
              display: flex;
              align-items: center;
              justify-content: center;
              box-shadow: 0px 3px 1px -1px rgba(26,26,26,0.07);
              position: relative;
              overflow: hidden;
            }
            .signup-btn::after {
              content: '';
              position: absolute;
              left: 0; top: 0; right: 0; bottom: 0;
              background: linear-gradient(90deg,rgba(255,255,255,0.07) 88%,rgba(255,255,255,0.15) 100%);
              pointer-events: none;
              border-radius: 8px;
            }
            .signup-btn:hover {
              background: #303030;
            }
            .signup-checkbox-wrapper {
              display: flex;
              align-items: flex-start;
              gap: 12px;
              cursor: pointer;
              margin-bottom: 8px;
            }
            .signup-checkbox {
              width: 18px;
              height: 18px;
              margin: 2px 0 0 0;
              accent-color: #1A1A1A;
              cursor: pointer;
            }
            .signup-checkbox-label {
              font-family: Inter, sans-serif;
              font-size: 14px;
              font-weight: 400;
              color: #303030;
              line-height: 1.43;
              text-align: left;
              cursor: pointer;
            }
            .otp-spinner {
              display: inline-block;
              width: 22px;
              height: 22px;
              border: 3px solid #fff;
              border-radius: 50%;
              border-top: 3px solid #1A1A1A;
              animation: otp-spin 0.7s linear infinite;
              vertical-align: middle;
              margin-right: 8px;
            }
            @keyframes otp-spin {
              0% { transform: rotate(0deg);}
              100% { transform: rotate(360deg);}
            }
          </style>
          <div class="signup-title">Contact Information</div>
          <div class="signup-desc">Please enter your email id to register and login</div>
          <form id="signup-form" autocomplete="off">
            ${registerFields === 'email' ? '' : `
            <div class="signup-form-row">
              <label class="signup-label" for="first_name">First name</label>
              <input name="first_name" id="first_name" placeholder="e.g John" class="signup-input" />
            </div>
            <div class="signup-form-row">
              <label class="signup-label" for="last_name">Last name</label>
              <input name="last_name" id="last_name" placeholder="e.g Doe" class="signup-input" />
            </div>
            `}
            <div class="signup-form-row">
              <label class="signup-label" for="email">Email Address *</label>
              <input name="email" id="email" placeholder="e.g name@domain.com" class="signup-input" type="email" required />
            </div>
            ${acceptEnabled ? `
            <div class="signup-form-row">
              <label class="signup-checkbox-wrapper">
                <input type="checkbox" name="subscription" id="subscription" class="signup-checkbox" />
                <span class="signup-checkbox-label">Subscribe to newsletter and promotional offers</span>
              </label>
            </div>
            ` : ''}
            <input type="hidden" name="phone" id="phone" value="${identifier}" />
            <button type="submit" class="signup-btn">Login</button>
          </form>
        `;
        // Apply dynamic button colors
        applyDynamicButtonColors();
        // Attach handler for the new signup form
        const signupForm = otpContainer.querySelector('#signup-form');
        signupForm.addEventListener('submit', async (e) => {
          e.preventDefault();
          const firstName = signupForm.querySelector('input[name="first_name"]')?.value.trim() || '';
          const lastName = signupForm.querySelector('input[name="last_name"]')?.value.trim() || '';
          const email = signupForm.querySelector('input[name="email"]')?.value.trim() || '';
          const phone = signupForm.querySelector('input[name="phone"]').value.trim();
          const subscription = signupForm.querySelector('input[name="subscription"]')?.checked || false;
          const submitBtn = signupForm.querySelector('button[type="submit"]');
          // Only require email
          if (!email) {
            alert('Please enter your email.');
            return;
          }
          submitBtn.disabled = true;
          submitBtn.innerHTML = '<span class="otp-spinner"></span>';
          const res = await fetch('/apps/zoketootp', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              phone,
              email,
              first_name: registerFields !== 'email' ? firstName : '',
              last_name: registerFields !== 'email' ? lastName : '',
              subscription,
              step: 'complete'
            })
          });
          const result = await res.json();
          submitBtn.disabled = false;
          if (result.success && result.email && result.password) {
            // Submit login form automatically with CAPTCHA
            const form = document.createElement('form');
            form.method = 'POST';
            form.action = '/account/login';
            const emailInput = document.createElement('input');
            emailInput.type = 'hidden';
            emailInput.name = 'customer[email]';
            emailInput.value = result.email;
            form.appendChild(emailInput);
            const passwordInput = document.createElement('input');
            passwordInput.type = 'hidden';
            passwordInput.name = 'customer[password]';
            passwordInput.value = result.password;
            form.appendChild(passwordInput);
            const formTypeInput = document.createElement('input');
            formTypeInput.type = 'hidden';
            formTypeInput.name = 'form_type';
            formTypeInput.value = 'customer_login';
            form.appendChild(formTypeInput);
            const urlParams = new URLSearchParams(window.location.search);
            const returnUrl = result.return_url || urlParams.get('return_url');
            if (returnUrl) {
              const returnInput = document.createElement('input');
              returnInput.type = 'hidden';
              returnInput.name = 'return_url';
              returnInput.value = returnUrl;
              form.appendChild(returnInput);
            }
            document.body.appendChild(form);
            if (window.Shopify && window.Shopify.captcha && typeof window.Shopify.captcha.protect === 'function') {
              window.Shopify.captcha.protect(form, () => { form.submit(); });
            } else {
              form.submit();
            }
            return;
          }
          if (result.error) {
            alert(result.error);
          } else {
            alert(result.message || 'Signup complete!');
          }
        });
        return;
      }
      // Success: login as usual
      if (result.success && result.email && result.password) {
        // Submit login form automatically with CAPTCHA
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = '/account/login';
        const emailInputHidden = document.createElement('input');
        emailInputHidden.type = 'hidden';
        emailInputHidden.name = 'customer[email]';
        emailInputHidden.value = result.email;
        form.appendChild(emailInputHidden);
        const passwordInput = document.createElement('input');
        passwordInput.type = 'hidden';
        passwordInput.name = 'customer[password]';
        passwordInput.value = result.password;
        form.appendChild(passwordInput);
        const formTypeInput = document.createElement('input');
        formTypeInput.type = 'hidden';
        formTypeInput.name = 'form_type';
        formTypeInput.value = 'customer_login';
        form.appendChild(formTypeInput);
        const urlParams = new URLSearchParams(window.location.search);
        const returnUrl = result.return_url || urlParams.get('return_url');
        if (returnUrl) {
          const returnInput = document.createElement('input');
          returnInput.type = 'hidden';
          returnInput.name = 'return_url';
          returnInput.value = returnUrl;
          form.appendChild(returnInput);
        }
        document.body.appendChild(form);
        if (window.Shopify && window.Shopify.captcha && typeof window.Shopify.captcha.protect === 'function') {
          window.Shopify.captcha.protect(form, () => { form.submit(); });
        } else {
          form.submit();
        }
        // Remove OTP UI immediately to avoid flicker on /account
        const otpContainerEl = document.getElementById('otp-login-container');
        if (otpContainerEl) otpContainerEl.remove();
        return;
      }
      if (result.error) {
        if (messageBox) {
          messageBox.style.color = 'red';
          messageBox.innerText = result.error;
        }
      } else if (result.message) {
        if (messageBox) {
          messageBox.style.color = 'green';
          messageBox.innerText = result.message;
        }
      }
      submitButton.innerHTML = step === 'complete' ? 'Continue' : 'Submit OTP';
      return;
    } else if (step === "update_email") {
      // On submit, if step is update_email, send correct payload
      if (!emailInput || !emailInput.value.trim()) {
        if (messageBox) {
          messageBox.style.color = "red";
          messageBox.innerText = "Please enter your email.";
        }
        return;
      }
      submitButton.disabled = true;
      submitButton.innerHTML = '<span class="otp-spinner"></span>';
      let payload = {
        step: "update_email",
        customerId: form.dataset.customerId,
        email: emailInput.value.trim(),
        phone: identifier
      };
      const res2 = await fetch("/apps/zoketootp", {
        method: "POST",
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const result2 = await res2.json();
      submitButton.disabled = false;
      // Handle login as normal
      if (result2.success && result2.email && result2.password) {
        // Submit login form automatically with CAPTCHA
        const formEl = document.createElement('form');
        formEl.method = 'POST';
        formEl.action = '/account/login';
        const emailInputHidden = document.createElement('input');
        emailInputHidden.type = 'hidden';
        emailInputHidden.name = 'customer[email]';
        emailInputHidden.value = result2.email;
        formEl.appendChild(emailInputHidden);
        const passwordInput = document.createElement('input');
        passwordInput.type = 'hidden';
        passwordInput.name = 'customer[password]';
        passwordInput.value = result2.password;
        formEl.appendChild(passwordInput);
        const formTypeInput = document.createElement('input');
        formTypeInput.type = 'hidden';
        formTypeInput.name = 'form_type';
        formTypeInput.value = 'customer_login';
        formEl.appendChild(formTypeInput);
        const urlParams = new URLSearchParams(window.location.search);
        const returnUrl = result2.return_url || urlParams.get('return_url');
        if (returnUrl) {
          const returnInput = document.createElement('input');
          returnInput.type = 'hidden';
          returnInput.name = 'return_url';
          returnInput.value = returnUrl;
          formEl.appendChild(returnInput);
        }
        document.body.appendChild(formEl);
        if (window.Shopify && window.Shopify.captcha && typeof window.Shopify.captcha.protect === 'function') {
          window.Shopify.captcha.protect(formEl, () => { formEl.submit(); });
        } else {
          formEl.submit();
        }
        // Remove OTP UI immediately to avoid flicker on /account
        const otpContainerEl2 = document.getElementById('otp-login-container');
        if (otpContainerEl2) otpContainerEl2.remove();
        return;
      }
      if (result2.error) {
        if (messageBox) {
          messageBox.style.color = 'red';
          messageBox.innerText = result2.error;
        }
      } else if (result2.message) {
        if (messageBox) {
          messageBox.style.color = 'green';
          messageBox.innerText = result2.message;
        }
      }
      submitButton.innerHTML = 'Continue';
      return;
    }
  });
}




/**
 * SECTION: SSO Management
 * Handles Single Sign-On (SSO) functionality for Google and Facebook
 */

/**
 * Manages SSO popup state to prevent multiple popups
 */
function isSSOPopupOpened() {
  return window._ssoPopupOpened || sessionStorage.getItem('ssoPopupOpened') === 'true';
}

function setSSOPopupOpened(val) {
  window._ssoPopupOpened = val;
  sessionStorage.setItem('ssoPopupOpened', val ? 'true' : 'false');
}

// Reset SSO popup state on page load
setSSOPopupOpened(false);

/**
 * SECTION: Google SSO Integration
 * Handles Google Single Sign-On functionality
 */

/**
 * Opens the Google SSO popup window
 */
function openGoogleSSOPopup() {
  console.log("openGoogleSSOPopup called");
  if (isSSOPopupOpened()) {
    console.log("SSO popup already opened, skipping");
    return;
  }
  setSSOPopupOpened(true);
  var shop = (window.Shopify && window.Shopify.shop) ? window.Shopify.shop : 'yourshop.myshopify.com';
  var urlParams = new URLSearchParams(window.location.search);
  var returnUrl = urlParams.get('return_url');
  var popupUrl = 'https://otp.zoketo.com/app/authgoogle?shop=' + encodeURIComponent(shop);
  if (returnUrl) {
    popupUrl += '&return_url=' + encodeURIComponent(returnUrl);
  }
  console.log("Opening SSO popup for shop:", shop, "with return_url:", returnUrl);
  var popup = window.open(popupUrl, 'googleSSO', 'width=500,height=600');
  
  // Monitor popup close event
  var checkClosed = setInterval(function() {
    if (popup.closed) {
      console.log("Google SSO popup was closed");
      setSSOPopupOpened(false);
      clearInterval(checkClosed);
    }
  }, 1000);
}

/**
 * Attaches click handlers to Google SSO buttons
 * Handles both static and dynamically added buttons
 */
function attachGoogleSSOHandlers() {
  document.querySelectorAll('.google-sso-btn').forEach(btn => {
    btn.removeEventListener('click', btn._googleSSOHandler);
    btn._googleSSOHandler = function (event) {
      console.log("Google SSO button clicked");
      event.preventDefault();
      openGoogleSSOPopup();
      return false;
    };
    btn.addEventListener('click', btn._googleSSOHandler);
  });
}

// Initial attach for Google SSO
attachGoogleSSOHandlers();

// Watch for dynamic UI changes (scoped to OTP container if available)
const googleSSOObserver = new MutationObserver(() => {
  attachGoogleSSOHandlers();
});
(function observeGoogleScope() {
  const scope = document.getElementById('otp-login-container') || document.body;
  googleSSOObserver.observe(scope, { childList: true, subtree: true });
})();

/**
 * SECTION: Facebook SSO Integration
 * Handles Facebook Single Sign-On functionality
 */

/**
 * Opens the Facebook SSO popup window
 */
function openFacebookSSOPopup() {
  console.log("openFacebookSSOPopup called");
  if (isSSOPopupOpened()) {
    console.log("SSO popup already opened, skipping");
    return;
  }
  setSSOPopupOpened(true);
  var shop = (window.Shopify && window.Shopify.shop) ? window.Shopify.shop : 'yourshop.myshopify.com';
  console.log("Opening Facebook SSO popup for shop:", shop);
  var popup = window.open('https://otp.zoketo.com/app/authfacebook?shop=' + encodeURIComponent(shop), 'facebookSSO', 'width=500,height=600');
  
  // Monitor popup close event
  var checkClosed = setInterval(function() {
    if (popup.closed) {
      console.log("Facebook SSO popup was closed");
      setSSOPopupOpened(false);
      clearInterval(checkClosed);
    }
  }, 1000);
}

/**
 * Attaches click handlers to Facebook SSO buttons
 * Handles both static and dynamically added buttons
 */
function attachFacebookSSOHandlers() {
  // Handle buttons with facebook-sso-btn class
  document.querySelectorAll('.facebook-sso-btn').forEach(btn => {
    // Remove any old onclick attributes
    btn.removeAttribute('onclick');
    btn.removeEventListener('click', btn._facebookSSOHandler);
    btn._facebookSSOHandler = function (event) {
      console.log("Facebook SSO button clicked");
      event.preventDefault();
      openFacebookSSOPopup();
      return false;
    };
    btn.addEventListener('click', btn._facebookSSOHandler);
  });
  
  // Also handle generic social buttons with Facebook images (fallback)
  document.querySelectorAll('.social-btn').forEach(btn => {
    if (btn.querySelector('img[alt="Facebook"]')) {
      // Remove any old onclick attributes
      btn.removeAttribute('onclick');
      btn.removeEventListener('click', btn._facebookSSOHandler);
      btn._facebookSSOHandler = function (event) {
        console.log("Facebook SSO button clicked");
        event.preventDefault();
        openFacebookSSOPopup();
        return false;
      };
      btn.addEventListener('click', btn._facebookSSOHandler);
    }
  });
}

// Initial attach for Facebook SSO
attachFacebookSSOHandlers();

// Watch for dynamic UI changes (scoped to OTP container if available)
const fbObserver = new MutationObserver(() => {
  attachFacebookSSOHandlers();
});
(function observeFacebookScope() {
  const scope = document.getElementById('otp-login-container') || document.body;
  fbObserver.observe(scope, { childList: true, subtree: true });
})();

/**
 * SECTION: SSO Message Handler
 * Processes messages from SSO popups and handles login
 */
window.addEventListener("message", function (event) {
  if (event.data && event.data.type === "shopify-sso-login") {
    setSSOPopupOpened(false); // Reset flag after successful login
    
    // Create and submit login form
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '/account/login';

    // Add login credentials
    const emailInput = document.createElement('input');
    emailInput.type = 'hidden';
    emailInput.name = 'customer[email]';
    emailInput.value = event.data.email;
    form.appendChild(emailInput);

    const passwordInput = document.createElement('input');
    passwordInput.type = 'hidden';
    passwordInput.name = 'customer[password]';
    passwordInput.value = event.data.password;
    form.appendChild(passwordInput);

    // Add form type for Shopify CAPTCHA
    const formTypeInput = document.createElement('input');
    formTypeInput.type = 'hidden';
    formTypeInput.name = 'form_type';
    formTypeInput.value = 'customer_login';
    form.appendChild(formTypeInput);

    // Use return_url from backend if present, else from query param
    const urlParams = new URLSearchParams(window.location.search);
    const returnUrl = event.data.return_url || urlParams.get('return_url');
    console.log("[SSO] Submitting login form with return_url:", returnUrl);
    if (returnUrl) {
      const returnInput = document.createElement('input');
      returnInput.type = 'hidden';
      returnInput.name = 'return_url';
      returnInput.value = returnUrl;
      form.appendChild(returnInput);
    }

    // Submit form with CAPTCHA protection if available
    document.body.appendChild(form);
    if (window.Shopify && window.Shopify.captcha && typeof window.Shopify.captcha.protect === 'function') {
      window.Shopify.captcha.protect(form, () => { form.submit(); });
    } else {
      form.submit();
    }
    // Remove OTP UI immediately to avoid flicker on /account
    const otpContainerEl3 = document.getElementById('otp-login-container');
    if (otpContainerEl3) otpContainerEl3.remove();
  }
});

/**
 * Renders the initial login/signup form with optional loading state for social buttons
 * @param {HTMLElement} otpContainer - The container to render into
 * @param {Object} options - { loadingSocial: boolean, prefillPhone: string }
 */
function renderInitialForm(otpContainer, options = {}) {
  const { loadingSocial = false, prefillPhone = '', selectedCountryCode = '' } = options;
  otpContainer.innerHTML = `
    <style>
      /* Force LTR direction to prevent RTL language switching issues */
      #otp-login-container,
      #otp-login-container * {
        direction: ltr !important;
        text-align: left !important;
        unicode-bidi: normal !important;
      }
      
      #otp-login-container h2 { 
        margin-top: 10px; 
        margin-bottom: 10px; 
        font-size: 30px; 
        font-weight: 700; 
        letter-spacing: -0.3px; 
        direction: ltr !important;
        text-align: left !important;
        unicode-bidi: normal !important;
      }
      .input-group {
        display: flex;
        align-items: center;
        border: 1px solid #ccc;
        border-radius: 8px;
        padding: 0 12px;
        background: #fff;
        margin-bottom: 8px;
        height: 48px;
        direction: ltr !important;
        text-align: left !important;
        unicode-bidi: normal !important;
      }
      .input-group select, .input-group input {
        background: transparent !important;
        border: none !important;
        box-shadow: none !important;
        outline: none !important;
        font-size: 16px;
        font-family: Inter, sans-serif;
        font-weight: 500;
        color: #222;
        margin: 0;
        padding: 0;
        direction: ltr !important;
        text-align: left !important;
        unicode-bidi: normal !important;
      }
      .otp-btn {
        width: 100%;
        background: #ffff;
        color: #fff;
        border: none;
        border-radius: 8px;
        height: 48px;
        font-size: 18px;
        margin-bottom: 10px;
        cursor: pointer;
        box-shadow: #ffff;
        transition: background 0.2s;
      }
      .otp-btn:hover { background: linear-gradient(180deg, #333 0%, #555 100%); }
      .divider { display: flex; align-items: center; text-align: center; margin-bottom: 12px; margin-top: 6px; }
      .divider::before, .divider::after { content: ''; flex: 1; border-bottom: 1.5px solid #ddd; }
      .divider:not(:empty)::before { margin-right: .75em; }
      .divider:not(:empty)::after { margin-left: .75em; }
      .social-row {
        display: flex;
        gap: 16px;
        margin-bottom: 8px;
      }
      .social-btn {
        flex: 1;
        display: flex !important;
        align-items: center;
        justify-content: center;
        background: #fff !important;
        border: 1px solid #ddd !important;
        border-radius: 8px !important;
        height: 48px !important;
        width: 100% !important;
        cursor: pointer;
        font-size: 1.1rem;
        transition: background 0.2s, border 0.2s;
        padding: 0 !important;
        margin: 0 !important;
        min-width: auto !important;
        max-width: none !important;
        box-sizing: border-box !important;
      }
      .social-btn:disabled {
        cursor: not-allowed;
      }
      .social-btn:hover {
        background: #f0f0f0;
        border-color: #bbb;
      }
      .social-btn img {
        width: 24px;
        height: 24px;
        display: block;
      }
      
      /* Mobile-specific overrides to prevent Shopify button CSS interference */
      @media (max-width: 768px) {
        .social-btn {
          display: flex !important;
          flex: 1 !important;
          width: 100% !important;
          min-width: 0 !important;
          max-width: none !important;
          padding: 0 !important;
          margin: 0 !important;
          background: #fff !important;
          border: 1px solid #ddd !important;
          border-radius: 8px !important;
          height: 48px !important;
          box-sizing: border-box !important;
          position: relative !important;
          overflow: visible !important;
        }
        
        .social-row {
          display: flex !important;
          gap: 16px !important;
          width: 100% !important;
          margin-bottom: 8px !important;
        }
        
        .social-btn img {
          width: 24px !important;
          height: 24px !important;
          display: block !important;
        }
      }
      .email-btn {
        width: 100% !important;       
        background: #fff !important;
        border: 1px solid #ddd !important;
        border-radius: 8px !important;
        line-height: 45px !important; 
        font-size: 18px !important;
        font-family: Inter, sans-serif !important;
        font-weight: 500 !important;
        cursor: pointer !important;
        color: #303030 !important;
        transition: background 0.2s, border 0.2s !important;
        margin: 0 !important;
        padding: 0 !important;
        box-shadow: none !important;
        outline: none !important;
        text-decoration: none !important;
        text-transform: none !important;
        letter-spacing: normal !important;
      }
      .email-btn:hover { 
        background: #f0f0f0 !important; 
        border-color: #bbb !important; 
      }
      .email-btn:focus { 
        outline: none !important; 
        box-shadow: none !important; 
      }
      .email-btn:active { 
        transform: none !important; 
      }
      /* More specific selector to override theme styles */
      button.email-btn,
      button[type="button"].email-btn,
      .otp-container button.email-btn {
        width: 100% !important;       
        background: #fff !important;
        border: 1px solid #ddd !important;
        border-radius: 8px !important;
        line-height: 45px !important; 
        font-size: 18px !important;
        font-family: Inter, sans-serif !important;
        font-weight: 500 !important;
        cursor: pointer !important;
        color: #303030 !important;
        transition: background 0.2s, border 0.2s !important;
        margin: 0 !important;
        padding: 0 !important;
        box-shadow: none !important;
        outline: none !important;
        text-decoration: none !important;
        text-transform: none !important;
        letter-spacing: normal !important;
      }
      button.email-btn:hover,
      button[type="button"].email-btn:hover,
      .otp-container button.email-btn:hover { 
        background: #f0f0f0 !important; 
        border-color: #bbb !important; 
      }
      button.email-btn:focus,
      button[type="button"].email-btn:focus,
      .otp-container button.email-btn:focus { 
        outline: none !important; 
        box-shadow: none !important; 
      }
      button.email-btn:active,
      button[type="button"].email-btn:active,
      .otp-container button.email-btn:active { 
        transform: none !important; 
      }
      .otp-spinner {
        display: inline-block;
        width: 22px;
        height: 22px;
        border: 3px solid #fff;
        border-radius: 50%;
        border-top: 3px solid #1A1A1A;
        animation: otp-spin 0.7s linear infinite;
        vertical-align: middle;
        margin-right: 8px;
      }
      @keyframes otp-spin {
        0% { transform: rotate(0deg);}
        100% { transform: rotate(360deg);}
      }
    </style>
    <h2 style="color: ${window.hyperOTPSettings?.loginHeaderColor || 'white'}; font-size: 22px;">${window.hyperOTPSettings?.loginHeader || 'Login/Signup'}</h2>
    <div id="otp-message" style="margin-bottom: 1rem; color: green;"></div>
    <form id="otp-form" autocomplete="off">
      <input type="hidden" name="step" value="send" />
      <div class="input-group">
        <select id="country-code">
            ${generateCountryOptions().map(country => 
              `<option value="${country.code}" ${selectedCountryCode && country.code === selectedCountryCode ? 'selected' : ''}>${country.code}</option>`
            ).join('')}
        </select>
        <input name="identifier" placeholder="  Phone Number" value="${prefillPhone ? prefillPhone : ''}" />
      </div>
      <button type="submit" class="otp-btn">Get OTP</button>
    </form>
    <div class="divider"><span style="font-size: 12px; font-weight: 600; color: #000000;">OR</span></div>
    <div class="social-row">
      ${!loadingSocial ? `
        ${getEnabledSocialLogins().google ? `
          <button type="button" class="social-btn google-sso-btn">
            <img src='https://img.icons8.com/color/48/google-logo.png' alt='Google' />
          </button>
        ` : ''}
        ${getEnabledSocialLogins().facebook ? `
          <button type="button" class="otp-social-btn social-btn facebook-sso-btn">
            <img src='https://img.icons8.com/fluency/48/facebook-new.png' alt='Facebook' />
            <span style="font-size:0;">Facebook</span>
          </button>
        ` : ''}
      ` : ''}
    </div>
        <button type="button" class="email-btn" disabled style="width: 100% !important; background: #fff !important; border: 1px solid #ddd !important; border-radius: 8px !important; line-height: 45px !important; font-size: 18px !important; font-family: Inter, sans-serif !important; font-weight: 500 !important; cursor: pointer !important; color: #303030 !important; margin: 0 !important; padding: 0 !important; box-shadow: none !important; outline: none !important; text-decoration: none !important; text-transform: none !important; letter-spacing: normal !important;">Login with Email &amp; Password</button>
        <div style="font-size: 10px; text-align: right; padding-top: 8px;">Powered by Zoketo</div>
  `;

  // Attach OTP form handler immediately
  const otpForm = otpContainer.querySelector('#otp-form');
  if (otpForm) {
    attachOtpFormHandler(otpForm, otpContainer);
    // Remove whitespace as user types in phone input
    const phoneInput = otpForm.querySelector('input[name="identifier"]');
    if (phoneInput) {
      phoneInput.addEventListener('input', function(e) {
        const cursor = phoneInput.selectionStart;
        const newValue = phoneInput.value.replace(/\s+/g, '');
        if (phoneInput.value !== newValue) {
          phoneInput.value = newValue;
          phoneInput.setSelectionRange(cursor - 1, cursor - 1);
        }
      });
    }
  }
  // Apply dynamic button colors
  applyDynamicButtonColors();
  
  // Force apply email button styles to prevent theme overrides
  const emailBtn = otpContainer.querySelector('.email-btn');
  if (emailBtn) {
    // Apply inline styles as a final fallback
    emailBtn.style.setProperty('width', '100%', 'important');
    emailBtn.style.setProperty('background', '#fff', 'important');
    emailBtn.style.setProperty('border', '1px solid #ddd', 'important');
    emailBtn.style.setProperty('border-radius', '8px', 'important');
    emailBtn.style.setProperty('line-height', '45px', 'important');
    emailBtn.style.setProperty('font-size', '18px', 'important');
    emailBtn.style.setProperty('font-family', 'Inter, sans-serif', 'important');
    emailBtn.style.setProperty('font-weight', '500', 'important');
    emailBtn.style.setProperty('cursor', 'pointer', 'important');
    emailBtn.style.setProperty('color', '#303030', 'important');
    emailBtn.style.setProperty('margin', '0', 'important');
    emailBtn.style.setProperty('padding', '0', 'important');
    emailBtn.style.setProperty('box-shadow', 'none', 'important');
    emailBtn.style.setProperty('outline', 'none', 'important');
    emailBtn.style.setProperty('text-decoration', 'none', 'important');
    emailBtn.style.setProperty('text-transform', 'none', 'important');
    emailBtn.style.setProperty('letter-spacing', 'normal', 'important');
    
    // Add event listeners to maintain styles on hover/focus
    emailBtn.addEventListener('mouseenter', function() {
      this.style.setProperty('background', '#f0f0f0', 'important');
      this.style.setProperty('border-color', '#bbb', 'important');
    });
    emailBtn.addEventListener('mouseleave', function() {
      this.style.setProperty('background', '#fff', 'important');
      this.style.setProperty('border-color', '#ddd', 'important');
    });
    emailBtn.addEventListener('focus', function() {
      this.style.setProperty('outline', 'none', 'important');
      this.style.setProperty('box-shadow', 'none', 'important');
    });
  }
}

// Apply settings to currently rendered OTP container (used for drawer and page flows)
function applySettingsToOTPContainer(otpContainer) {
  try {
    const settings = window.hyperOTPSettings;
    if (!settings || !otpContainer) return;
    const { google, facebook } = getEnabledSocialLogins();
    const showOTPForm = shouldShowOTPForm();
    const showEmailPassword = shouldShowEmailPasswordLogin();
    const googleButton = otpContainer.querySelector('.google-sso-btn');
    const facebookButton = otpContainer.querySelector('button:has(img[alt="Facebook"])');
    const emailButton = otpContainer.querySelector('.email-btn, .otp-email-btn');
    const divider = otpContainer.querySelector('.divider, .otp-divider');
    const otpFormElement = otpContainer.querySelector('#otp-form');
    if (googleButton) {
      if (google) { googleButton.disabled = false; googleButton.style.opacity = '1'; googleButton.style.display = ''; }
      else { googleButton.style.display = 'none'; }
    }
    if (facebookButton) {
      if (facebook) { facebookButton.disabled = false; facebookButton.style.opacity = '1'; facebookButton.style.display = ''; }
      else { facebookButton.style.display = 'none'; }
    }
    if (emailButton) {
      if (showEmailPassword) { emailButton.disabled = false; emailButton.style.opacity = '1'; emailButton.style.display = ''; }
      else { emailButton.style.display = 'none'; }
    }
    if (otpFormElement) { otpFormElement.style.display = showOTPForm ? 'block' : 'none'; }
    if (divider && !google && !facebook && !showEmailPassword) { divider.style.display = 'none'; }
    attachGoogleSSOHandlers();
    attachFacebookSSOHandlers();
    applyDynamicButtonColors();
  } catch (_) {}
}

// Function to apply dynamic button colors based on settings
function applyDynamicButtonColors() {
  const settings = window.hyperOTPSettings;
  if (!settings) return;
  
  const buttonColor = settings.buttonColor || '#ffff';
  const textColor = settings.textColor || '#ffffff';
  
  // Create or update dynamic button styles
  let dynamicButtonStyle = document.getElementById('hyperotp-dynamic-button-styles');
  if (!dynamicButtonStyle) {
    dynamicButtonStyle = document.createElement('style');
    dynamicButtonStyle.id = 'hyperotp-dynamic-button-styles';
    document.head.appendChild(dynamicButtonStyle);
  }
  
  // Apply dynamic colors to all buttons
  dynamicButtonStyle.innerHTML = `
    .otp-btn {
      background: ${buttonColor} !important;
      color: ${textColor} !important;
      box-shadow: none !important;
    }
    .otp-btn:hover {
      background: ${adjustColorBrightness(buttonColor, -20)} !important;
      box-shadow: none !important;
    }
    .otp-verify-btn {
      background: ${buttonColor} !important;
      color: ${textColor} !important;
      box-shadow: none !important;
    }
    .otp-verify-btn:hover {
      background: ${adjustColorBrightness(buttonColor, -20)} !important;
      box-shadow: none !important;
    }
    .signup-btn {
      background: ${buttonColor} !important;
      color: ${textColor} !important;
      box-shadow: none !important;
    }
    .signup-btn:hover {
      background: ${adjustColorBrightness(buttonColor, -20)} !important;
      box-shadow: none !important;
    }
  `;
}

// Helper function to adjust color brightness
function adjustColorBrightness(hex, percent) {
  // Remove # if present
  hex = hex.replace('#', '');
  
  // Parse the hex color
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // Adjust brightness
  const factor = 1 + (percent / 100);
  const newR = Math.min(255, Math.max(0, Math.round(r * factor)));
  const newG = Math.min(255, Math.max(0, Math.round(g * factor)));
  const newB = Math.min(255, Math.max(0, Math.round(b * factor)));
  
  // Convert back to hex
  return '#' + 
    (newR < 16 ? '0' : '') + newR.toString(16) +
    (newG < 16 ? '0' : '') + newG.toString(16) +
    (newB < 16 ? '0' : '') + newB.toString(16);
}

// Function to inject CSS to hide Shopify login form, only if appEnabled is true
function injectHideLoginCSS() {
  if (document.getElementById('hyperotp-hide-login-style')) return;
  const style = document.createElement("style");
  style.id = "hyperotp-hide-login-style";
  style.innerHTML = `
    form[action="/account/login"],
    #customer_login,
    .customer.login,
    [data-login-form],
    .shopify-section--main-password,
    .shopify-section--main-login,
    .section-header__title,
    .login-title,
    .form-title,
    .customer-login-title,
    form[action="/account/login"] *,
    #customer_login h1,
    #customer_login h2,
    .customer.login h1,
    .customer.login h2,
    .login-form,
    .login-form *,
    .container .row .col-lg-6:has(.login-form),
    .container .row .col-lg-6 .login-form,
    .container .row .col-lg-6 h2,
    .container .row .col-lg-6 form,
    .container .row .col-lg-6 .form-group,
    .container .row .col-lg-6 .btn,
    #CustomerLoginForm,
    #CustomerLoginForm *,
    .account-form,
    .account-form *,
    h1.title.row {
      display: none !important;
      visibility: hidden !important;
      height: 0 !important;
      opacity: 0 !important;
      pointer-events: none !important;
      position: absolute !important;
      z-index: -1 !important;
    }
  `;
  document.head.appendChild(style);
}

// Function to show Shopify default login form and remove custom UI
function showShopifyDefaultLoginForm() {
  // Remove custom OTP UI with fade-out
  const otpContainer = document.getElementById('otp-login-container');
  if (otpContainer) {
    otpContainer.classList.add('otp-fade-out');
    setTimeout(() => {
      otpContainer.remove();
      // Remove both temp and permanent hide-login CSS
      const tempStyle = document.getElementById('hyperotp-temp-hide-login-style');
      if (tempStyle) tempStyle.remove();
      const permStyle = document.getElementById('hyperotp-hide-login-style');
      if (permStyle) permStyle.remove();
      // Optionally scroll to login form
      const loginForm = document.querySelector('form[action="/account/login"]') || 
                       document.querySelector('.login-form') ||
                       document.querySelector('.container .row .col-lg-6');
      if (loginForm) loginForm.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 120); // match fade-out duration
  } else {
    // Remove both temp and permanent hide-login CSS
    const tempStyle = document.getElementById('hyperotp-temp-hide-login-style');
    if (tempStyle) tempStyle.remove();
    const permStyle = document.getElementById('hyperotp-hide-login-style');
    if (permStyle) permStyle.remove();
    // Optionally scroll to login form
    const loginForm = document.querySelector('form[action="/account/login"]') || 
                     document.querySelector('.login-form') ||
                     document.querySelector('.container .row .col-lg-6');
    if (loginForm) loginForm.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
}

// Attach handler to all email/password login buttons
function attachEmailPasswordHandlers() {
  document.querySelectorAll('.otp-email-btn, .email-btn').forEach(btn => {
    btn.removeEventListener('click', btn._emailLoginHandler);
    btn._emailLoginHandler = function (event) {
      event.preventDefault();
      showShopifyDefaultLoginForm();
      return false;
    };
    btn.addEventListener('click', btn._emailLoginHandler);
  });
}

// Initial attach for email/password login
attachEmailPasswordHandlers();

// Watch for dynamic UI changes (scoped to OTP container if available)
const emailBtnObserver = new MutationObserver(() => {
  attachEmailPasswordHandlers();
});
(function observeEmailScope() {
  const scope = document.getElementById('otp-login-container') || document.body;
  emailBtnObserver.observe(scope, { childList: true, subtree: true });
})();

// Add fade-out CSS for smooth transition
(function addOtpFadeOutCSS() {
  if (document.getElementById('otp-fade-out-style')) return;
  const style = document.createElement('style');
  style.id = 'otp-fade-out-style';
  style.innerHTML = `
    #otp-login-container.otp-fade-out {
      opacity: 0;
      transition: opacity 0.12s ease;
    }
  `;
  document.head.appendChild(style);
})();

// Add fade-in CSS for OTP UI (in case of SSR or style removal)
(function addOtpFadeInCSS() {
  if (document.getElementById('otp-fade-in-style')) return;
  const style = document.createElement('style');
  style.id = 'otp-fade-in-style';
  style.innerHTML = `
    .otp-fade-in {
      opacity: 0;
      transition: opacity 0.33s cubic-bezier(.4,0,.2,1);
    }
    .otp-fade-in[style*="opacity: 1"] {
      opacity: 1 !important;
    }
  `;
  document.head.appendChild(style);
})();

// Add mobile margin/padding for OTP container and RTL protection
(function injectOtpMobileMarginCSS() {
  if (document.getElementById('otp-login-mobile-style')) return;
  const style = document.createElement('style');
  style.id = 'otp-login-mobile-style';
  style.innerHTML = `
    /* Force LTR direction for OTP container to prevent RTL language switching issues */
    #otp-login-container,
    #otp-login-container *,
    #otp-login-container h2,
    #otp-login-container form,
    #otp-login-container input,
    #otp-login-container select,
    #otp-login-container button,
    #otp-login-container div,
    #otp-login-container span,
    #otp-login-container p,
    #otp-login-container label {
      direction: ltr !important;
      text-align: left !important;
      unicode-bidi: normal !important;
    }
    
    /* Specific overrides for form elements */
    #otp-login-container .input-group {
      direction: ltr !important;
      text-align: left !important;
    }
    
    #otp-login-container .input-group input,
    #otp-login-container .input-group select {
      direction: ltr !important;
      text-align: left !important;
      unicode-bidi: normal !important;
    }
    
    #otp-login-container .otp-verify-input,
    #otp-login-container .signup-input,
    #otp-login-container .email-input {
      direction: ltr !important;
      text-align: left !important;
      unicode-bidi: normal !important;
    }
    
    #otp-login-container .otp-verify-input::placeholder,
    #otp-login-container .signup-input::placeholder,
    #otp-login-container .email-input::placeholder {
      direction: ltr !important;
      text-align: left !important;
      unicode-bidi: normal !important;
    }
    
    /* Force LTR for all text content */
    #otp-login-container .otp-title,
    #otp-login-container .otp-verify-title,
    #otp-login-container .signup-title,
    #otp-login-container .otp-verify-desc,
    #otp-login-container .signup-desc,
    #otp-login-container .otp-verify-change-label,
    #otp-login-container .signup-label,
    #otp-login-container .otp-verify-bottom-label,
    #otp-login-container .otp-verify-bottom-link,
    #otp-login-container .otp-divider-text {
      direction: ltr !important;
      text-align: left !important;
      unicode-bidi: normal !important;
    }
    
    /* Force LTR for buttons */
    #otp-login-container .otp-btn,
    #otp-login-container .otp-verify-btn,
    #otp-login-container .signup-btn,
    #otp-login-container .social-btn,
    #otp-login-container .email-btn {
      direction: ltr !important;
      text-align: center !important;
      unicode-bidi: normal !important;
    }
    
    /* Force LTR for form rows and containers */
    #otp-login-container .otp-verify-form-row,
    #otp-login-container .signup-form-row,
    #otp-login-container .social-row,
    #otp-login-container .otp-verify-bottom-row {
      direction: ltr !important;
      text-align: left !important;
    }
    
    #otp-login-container {
      position: relative !important;
      // min-height: 400px !important;
      padding-bottom: 12px !important;
      direction: ltr !important;
      text-align: left !important;
    }
    @media (max-width: 600px) {
      #otp-login-container {
        margin: 26px 36px !important;
        padding: 12px 8px 8px 8px !important;
        max-width: 98vw !important;
        position: relative !important;
        overflow: hidden !important;
        direction: ltr !important;
        text-align: left !important;
      }
      .otp-verify-form-row {
        width: 100% !important;
        max-width: 100% !important;
        overflow: hidden !important;
        direction: ltr !important;
        text-align: left !important;
      }
      .otp-verify-input {
        font-size: 20px !important;
        letter-spacing: 0.4em !important;
        height: 50px !important;
        margin-top: 0 !important;
        max-width: 100% !important;
        width: 100% !important;
        box-sizing: border-box !important;
        direction: ltr !important;
        text-align: left !important;
        unicode-bidi: normal !important;
      }
      .otp-verify-input::placeholder {
        font-size: 14px !important;
        letter-spacing: normal !important;
        direction: ltr !important;
        text-align: left !important;
        unicode-bidi: normal !important;
      }
      .email-input {
        font-size: 16px !important;
        letter-spacing: normal !important;
        text-align: left !important;
        padding: 0 12px !important;
        direction: ltr !important;
        unicode-bidi: normal !important;
      }
      .email-input::placeholder {
        font-size: 14px !important;
        letter-spacing: normal !important;
        direction: ltr !important;
        text-align: left !important;
        unicode-bidi: normal !important;
      }
    }
  `;
  document.head.appendChild(style);
})();

/**
 * SECTION: Login Drawer Element Hiding
 * Hides specific login form elements when login drawer is opened
 */

// Function to hide login drawer elements and move OTP form into drawer
function hideLoginDrawerElements() {
  console.log("🎭 Hiding login drawer elements and moving OTP form...");
  
  // Target the specific elements mentioned in the user's request
  const loginSidebar = document.getElementById('login_login-sidebar');
  const loginDrawer = document.getElementById('t4s-login-sidebar');
  if (loginSidebar) {
    loginSidebar.style.display = 'none';
    console.log("✅ Hidden login_login-sidebar element");
  }
  
  // Also hide the form within it
  const customerLoginForm = document.getElementById('customer_loginlogin-sidebar');
  if (customerLoginForm) {
    customerLoginForm.style.display = 'none';
    console.log("✅ Hidden customer_loginlogin-sidebar form");
  }
  
  // Ensure OTP container exists; if not, create and render it immediately
  if (!document.getElementById('otp-login-container')) {
    if (!loginDrawer) {
      console.log('⚠️ Drawer not found; abort creating OTP container.');
      return;
    }
    console.log('🧩 Creating OTP container for drawer on open...');
    const otpContainer = document.createElement('div');
    otpContainer.id = 'otp-login-container';
    otpContainer.className = 'otp-fade-in';
    otpContainer.style.cssText = `
      background: #fff;
      max-width: 100%;
      width: 100%;
      margin: 0;
      border-radius: 8px;
      box-shadow: none;
      padding: 20px;
      font-family: 'Inter', sans-serif;
      opacity: 0;
      transition: opacity 0.33s cubic-bezier(.4,0,.2,1);
      box-sizing: border-box;
      overflow: hidden;
    `;
    // Render with available settings if present; else show loading state
    if (window.hyperOTPSettings) {
      renderInitialForm(otpContainer, { loadingSocial: false });
      applySettingsToOTPContainer(otpContainer);
    } else {
      renderInitialForm(otpContainer, { loadingSocial: true });
      // When settings resolve later, existing global handlers will apply colors/buttons
      hyperOTPSettingsPromise && hyperOTPSettingsPromise.then((settings) => {
        if (document.getElementById('otp-login-container')) {
          renderInitialForm(otpContainer, { loadingSocial: false });
          applySettingsToOTPContainer(otpContainer);
        }
      });
    }
    // Insert directly into drawer content
    let drawerContent = loginDrawer.querySelector('.t4s-drawer__content') || 
                       loginDrawer.querySelector('.drawer-content') ||
                       loginDrawer.querySelector('.t4s-content-login-sidebar') ||
                       loginDrawer.querySelector('[class*="content"]') ||
                       loginDrawer;
    drawerContent.insertBefore(otpContainer, drawerContent.firstChild);
    setTimeout(() => { otpContainer.style.opacity = '1'; }, 10);
  }
  
  // Move existing OTP form into the drawer
  moveOTPFormToDrawer();
}

// Function to move existing OTP form into the drawer
function moveOTPFormToDrawer() {
  const loginDrawer = document.getElementById('t4s-login-sidebar');
  const existingOTPContainer = document.getElementById('otp-login-container');
  
  if (!loginDrawer) {
    console.log("⚠️ Login drawer not found");
    return;
  }
  
  if (!existingOTPContainer) {
    console.log("⚠️ Existing OTP container not found");
    return;
  }
  
  // Check if OTP form is already moved to drawer
  if (loginDrawer.querySelector('#otp-login-container')) {
    console.log("✅ OTP form already in drawer");
    return;
  }
  
  console.log("🎨 Moving existing OTP form into drawer...");
  
  // Adjust styling for drawer
  existingOTPContainer.style.cssText = `
    position: relative !important;
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
    padding: 20px !important;
    background: #fff !important;
    border-radius: 8px !important;
    box-shadow: none !important;
    font-family: 'Inter', sans-serif !important;
    opacity: 1 !important;
    order: -1 !important;
    box-sizing: border-box !important;
    overflow: hidden !important;
  `;
  
  // Add drawer-specific CSS to fix input field widths
  const drawerStyle = document.createElement('style');
  drawerStyle.id = 'otp-drawer-fix-style';
  drawerStyle.innerHTML = `
    #otp-login-container .input-group {
      width: 100% !important;
      max-width: 100% !important;
      box-sizing: border-box !important;
      display: flex !important;
      align-items: center !important;
    }
    #otp-login-container .input-group input {
      flex: 1 !important;
      min-width: 0 !important;
      width: auto !important;
      max-width: none !important;
      box-sizing: border-box !important;
      border: none !important;
      outline: none !important;
      background: transparent !important;
      font-size: 16px !important;
      font-family: Inter, sans-serif !important;
      font-weight: 500 !important;
      color: #222 !important;
      margin: 0 !important;
      padding: 0 !important;
    }
    #otp-login-container .input-group select {
      flex-shrink: 0 !important;
      min-width: 33px !important;
      width: auto !important;
      border: none !important;
      outline: none !important;
      background: transparent !important;
      font-size: 16px !important;
      font-family: Inter, sans-serif !important;
      font-weight: 500 !important;
      color: #222 !important;
      margin: 0 !important;
      padding: 0 !important;
    }
    #otp-login-container .otp-btn,
    #otp-login-container .social-btn,
    #otp-login-container .email-btn {
      width: 100% !important;
      max-width: 100% !important;
      box-sizing: border-box !important;
    }
    #otp-login-container .social-row {
      width: 100% !important;
      max-width: 100% !important;
      box-sizing: border-box !important;
    }
    #otp-login-container .otp-verify-input {
      width: 100% !important;
      max-width: 100% !important;
      box-sizing: border-box !important;
    }
    #otp-login-container .otp-verify-form-row {
      width: 100% !important;
      max-width: 100% !important;
      box-sizing: border-box !important;
    }
  `;
  
  // Remove existing style if present
  const existingStyle = document.getElementById('otp-drawer-fix-style');
  if (existingStyle) {
    existingStyle.remove();
  }
  
  // Add the style to document head
  document.head.appendChild(drawerStyle);
  
  // Find the drawer content area - be more specific to avoid affecting other elements
  let drawerContent = loginDrawer.querySelector('.t4s-drawer__content') || 
                     loginDrawer.querySelector('.drawer-content') ||
                     loginDrawer.querySelector('.t4s-content-login-sidebar') ||
                     loginDrawer.querySelector('[class*="content"]');
  
  if (!drawerContent) {
    // If no content area found, look for the login sidebar specifically
    const loginSidebar = loginDrawer.querySelector('#login_login-sidebar');
    if (loginSidebar) {
      // Create a wrapper div inside the login sidebar
      drawerContent = document.createElement('div');
      drawerContent.className = 'otp-drawer-wrapper';
      drawerContent.style.cssText = `
        padding: 20px;
        width: 100%;
        box-sizing: border-box;
      `;
      
      // Insert the wrapper at the beginning of the login sidebar
      loginSidebar.insertBefore(drawerContent, loginSidebar.firstChild);
    } else {
      // Last resort: create content area but be more careful
      drawerContent = document.createElement('div');
      drawerContent.className = 'drawer-content';
      drawerContent.style.cssText = `
        padding: 20px;
        height: 100%;
        overflow-y: auto;
        position: relative;
        z-index: 1;
      `;
      
      // Only move login-related children, preserve other elements
      const childrenToMove = Array.from(loginDrawer.children).filter(child => 
        child.id.includes('login') || 
        child.className.includes('login') ||
        child.className.includes('t4s-content')
      );
      
      childrenToMove.forEach(child => {
        drawerContent.appendChild(child);
      });
      
      // Add the content area to the drawer
      loginDrawer.appendChild(drawerContent);
    }
  }
  
  // Move the OTP container to the top of the drawer content area
  drawerContent.insertBefore(existingOTPContainer, drawerContent.firstChild);
  
  console.log("✅ Existing OTP form moved into drawer");
}





// Function to show login drawer elements and move OTP form back
function showLoginDrawerElements() {
  console.log("🎭 Showing login drawer elements and moving OTP form back...");
  
  const loginSidebar = document.getElementById('login_login-sidebar');
  if (loginSidebar) {
    loginSidebar.style.display = '';
    console.log("✅ Shown login_login-sidebar element");
  }
  
  const customerLoginForm = document.getElementById('customer_loginlogin-sidebar');
  if (customerLoginForm) {
    customerLoginForm.style.display = '';
    console.log("✅ Shown customer_loginlogin-sidebar form");
  }
  
  // Move OTP form back to its original location
  moveOTPFormBackToOriginalLocation();
}

// Function to move OTP form back to its original location
function moveOTPFormBackToOriginalLocation() {
  const loginDrawer = document.getElementById('t4s-login-sidebar');
  const existingOTPContainer = document.getElementById('otp-login-container');
  
  if (!loginDrawer || !existingOTPContainer) {
    return;
  }
  
  // Check if OTP form is in the drawer
  if (!loginDrawer.querySelector('#otp-login-container')) {
    console.log("✅ OTP form already in original location");
    return;
  }
  
  console.log("🎨 Moving OTP form back to original location...");

  // If not on account pages, remove OTP container instead of moving back
  try {
    const path = window.location && window.location.pathname ? window.location.pathname : '';
    const isAccountPage = /^\/account(\/login)?\/?$/.test(path);
    if (!isAccountPage) {
      const tempStyle = document.getElementById('hyperotp-temp-hide-login-style');
      if (tempStyle) tempStyle.remove();
      const permStyle = document.getElementById('hyperotp-hide-login-style');
      if (permStyle) permStyle.remove();
      existingOTPContainer.remove();
      console.log('🧹 Removed OTP container after drawer close on non-account page');
      return;
    }
  } catch (_) {
    // if any error in path check, default to remove to be safe on non-account
    try { existingOTPContainer.remove(); } catch (_) {}
    return;
  }
  
  // Remove drawer-specific styles
  const drawerStyle = document.getElementById('otp-drawer-fix-style');
  if (drawerStyle) {
    drawerStyle.remove();
  }
  
  // Clean up any wrapper divs we created
  const wrapperDiv = document.querySelector('.otp-drawer-wrapper');
  if (wrapperDiv) {
    // Move any remaining content back to its parent
    while (wrapperDiv.firstChild) {
      wrapperDiv.parentNode.insertBefore(wrapperDiv.firstChild, wrapperDiv);
    }
    wrapperDiv.remove();
  }
  
  // Restore original styling
  existingOTPContainer.style.cssText = `
    background: #fff;
    max-width: 470px;
    margin: 40px auto;
    border-radius: 16px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.07);
    padding: 12px 24px 24px 24px;
    font-family: 'Inter', sans-serif;
    opacity: 1;
    transition: opacity 0.33s cubic-bezier(.4,0,.2,1);
  `;
  
  // Find the original container and move the OTP form back
  const originalContainer = findBestContainer();
  if (originalContainer) {
    originalContainer.appendChild(existingOTPContainer);
    console.log("✅ OTP form moved back to original location");
  }
}

// Function to check if login drawer is open
function isLoginDrawerOpen() {
  const loginDrawer = document.getElementById('t4s-login-sidebar');
  if (loginDrawer) {
    const ariaHidden = loginDrawer.getAttribute('aria-hidden');
    return ariaHidden === 'false';
  }
  return false;
}

// Observer to watch for login drawer state changes
function observeLoginDrawerChanges() {
  const loginDrawer = document.getElementById('t4s-login-sidebar');
  if (!loginDrawer) {
    console.log("⚠️ Login drawer not found, retrying in 1 second...");
    setTimeout(observeLoginDrawerChanges, 1000);
    return;
  }
  
  // Prevent multiple observers on the same element
  if (loginDrawer._otpObserverAttached) {
    console.log("✅ Observer already attached to login drawer");
    return;
  }
  
  console.log("👀 Observing login drawer changes...");
  
  // Create a mutation observer to watch for aria-hidden changes
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes' && mutation.attributeName === 'aria-hidden') {
        const isOpen = mutation.target.getAttribute('aria-hidden') === 'false';
        console.log(`🎭 Login drawer ${isOpen ? 'opened' : 'closed'}`);
        
        if (isOpen) {
          // Hide the elements when drawer opens and move OTP form into drawer
          hideLoginDrawerElements();
        } else {
          // Show the elements when drawer closes and move OTP form back or remove on non-account
          showLoginDrawerElements();
        }
      }
    });
  });
  
  // Mark as attached and start observing
  loginDrawer._otpObserverAttached = true;
  observer.observe(loginDrawer, {
    attributes: true,
    attributeFilter: ['aria-hidden']
  });
  
  // Also check initial state
  if (isLoginDrawerOpen()) {
    hideLoginDrawerElements();
  }
}

// Initialize the login drawer observer
(function initializeLoginDrawerObserver() {
  console.log("🚀 Initializing login drawer observer...");
  
  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeLoginDrawerChanges);
  } else {
    observeLoginDrawerChanges();
  }
  
  // Additional safety check to prevent Shopify default form from showing
  setTimeout(() => {
    const loginDrawer = document.getElementById('t4s-login-sidebar');
    if (loginDrawer && loginDrawer.getAttribute('aria-hidden') === 'false') {
      // If drawer is open but we haven't processed it yet, do it now
      hideLoginDrawerElements();
      // Ensure OTP container exists in drawer if not present
      if (!document.getElementById('otp-login-container')) {
        const tmp = document.createElement('div');
        tmp.id = 'otp-login-container';
        tmp.className = 'otp-fade-in';
        tmp.style.cssText = `
          background: #fff;
          max-width: 100%;
          width: 100%;
          margin: 0;
          border-radius: 8px;
          box-shadow: none;
          padding: 20px;
          font-family: 'Inter', sans-serif;
          opacity: 0;
          transition: opacity 0.33s cubic-bezier(.4,0,.2,1);
        `;
        if (window.hyperOTPSettings) {
          renderInitialForm(tmp, { loadingSocial: false });
          applySettingsToOTPContainer(tmp);
        } else {
          renderInitialForm(tmp, { loadingSocial: true });
          hyperOTPSettingsPromise && hyperOTPSettingsPromise.then(() => {
            if (document.getElementById('otp-login-container')) {
              renderInitialForm(tmp, { loadingSocial: false });
              applySettingsToOTPContainer(tmp);
            }
          });
        }
        const drawerContent = loginDrawer.querySelector('.t4s-drawer__content') || 
                              loginDrawer.querySelector('.drawer-content') ||
                              loginDrawer.querySelector('.t4s-content-login-sidebar') ||
                              loginDrawer.querySelector('[class*="content"]') ||
                              loginDrawer;
        drawerContent.insertBefore(tmp, drawerContent.firstChild);
        setTimeout(() => { tmp.style.opacity = '1'; }, 10);
      }
    }
  }, 100);
})();

// Ultra-aggressive approach to override theme CSS on select elements
(function overrideThemeSelectCSS() {
  let reapplyScheduled = false;
  let isApplying = false;

  function injectUltraSpecificCSS() {
    // Create or update ultra-specific CSS that targets select elements directly
    const existingStyle = document.getElementById('hyperotp-ultra-specific-select-override');
    const cssContent = `
      /* Ultra-specific selectors to override theme CSS */
      select,
      select#country-code,
      #country-code,
      .hyperotp-country-select,
      .country-code-wrapper select,
      .input-group select,
      #otp-login-container select,
      #otp-login-container .input-group select,
      #otp-login-container select#country-code,
      body select,
      html body select,
      html body select#country-code,
      html body #otp-login-container select,
      html body #otp-login-container select#country-code,
      html body #otp-login-container .input-group select,
      html body #otp-login-container .input-group select#country-code {
        width: 40px !important;
        min-width: 40px !important;
        max-width: 40px !important;
        height: 48px !important;
        padding: 0px !important;
        border: none !important;
        background: transparent !important;
        background-color: transparent !important;
        background-image: none !important;
        background-position: initial !important;
        background-size: initial !important;
        background-repeat: initial !important;
        font-size: 16px !important;
        font-family: Inter, sans-serif !important;
        font-weight: 500 !important;
        color: #222 !important;
        margin: 0 !important;
        outline: none !important;
        box-shadow: none !important;
        appearance: none !important;
        -webkit-appearance: none !important;
        -moz-appearance: none !important;
        vertical-align: middle !important;
        transition: none !important;
        display: inline-block !important;
        flex-shrink: 0 !important;
        flex-grow: 0 !important;
        flex-basis: 40px !important;
        box-sizing: border-box !important;
        text-align: center !important;
        cursor: pointer !important;
        direction: ltr !important;
        unicode-bidi: normal !important;
      }
      
      /* Ensure input takes remaining space */
      #otp-login-container .input-group input[name="identifier"],
      #otp-login-container .input-group input,
      .input-group input[name="identifier"],
      .input-group input {
        flex: 1 !important;
        min-width: 0 !important;
        width: auto !important;
        max-width: none !important;
        flex-shrink: 1 !important;
        flex-grow: 1 !important;
        flex-basis: auto !important;
        background: transparent !important;
        border: none !important;
        box-shadow: none !important;
        outline: none !important;
        font-size: 16px !important;
        font-family: Inter, sans-serif !important;
        font-weight: 500 !important;
        color: #222 !important;
        margin: 0 !important;
        padding: 0 !important;
        direction: ltr !important;
        text-align: left !important;
        unicode-bidi: normal !important;
        box-sizing: border-box !important;
      }
      
      /* Ensure input-group maintains proper flex layout */
      #otp-login-container .input-group,
      .input-group {
        display: flex !important;
        align-items: center !important;
        width: 100% !important;
        max-width: 100% !important;
        box-sizing: border-box !important;
        direction: ltr !important;
        text-align: left !important;
        unicode-bidi: normal !important;
      }
    `;
    
    if (existingStyle) {
      if (existingStyle.innerHTML !== cssContent) {
        existingStyle.innerHTML = cssContent;
      }
    } else {
      const style = document.createElement('style');
      style.id = 'hyperotp-ultra-specific-select-override';
      style.innerHTML = cssContent;
    // Insert at the very end of head to ensure it loads last
    document.head.appendChild(style);
    }
  }

  function forceInlineStyles() {
    // Find all country code dropdowns and force inline styles
    const countrySelects = document.querySelectorAll('select#country-code, #country-code, .hyperotp-country-select, .country-code-wrapper select, .input-group select');
    
    countrySelects.forEach(select => {
      // Force inline styles with maximum specificity
      const desired = {
        width: '40px',
        'min-width': '40px',
        'max-width': '40px',
        height: '48px',
        padding: '0px',
        border: 'none',
        background: 'transparent',
        'background-color': 'transparent',
        'background-image': 'none',
        'background-position': 'initial',
        'background-size': 'initial',
        'background-repeat': 'initial',
        'font-size': '16px',
        'font-family': 'Inter, sans-serif',
        'font-weight': '500',
        color: '#222',
        margin: '0',
        outline: 'none',
        'box-shadow': 'none',
        appearance: 'none',
        '-webkit-appearance': 'none',
        '-moz-appearance': 'none',
        'vertical-align': 'middle',
        transition: 'none',
        display: 'inline-block',
        'flex-shrink': '0',
        'flex-grow': '0',
        'flex-basis': '40px',
        'box-sizing': 'border-box',
        'text-align': 'center',
        cursor: 'pointer',
        direction: 'ltr',
        'unicode-bidi': 'normal'
      };
      Object.keys(desired).forEach(key => {
        if (select.style.getPropertyValue(key) !== desired[key]) {
          select.style.setProperty(key, desired[key], 'important');
        }
      });
    });

    // Force input styles
    const phoneInputs = document.querySelectorAll('#otp-login-container .input-group input[name="identifier"], .input-group input[name="identifier"]');
    phoneInputs.forEach(input => {
      const desired = {
        flex: '1',
        'min-width': '0',
        width: 'auto',
        'max-width': 'none',
        'flex-shrink': '1',
        'flex-grow': '1',
        'flex-basis': 'auto'
      };
      Object.keys(desired).forEach(key => {
        if (input.style.getPropertyValue(key) !== desired[key]) {
          input.style.setProperty(key, desired[key], 'important');
        }
      });
    });

    // Force input-group styles
    const inputGroups = document.querySelectorAll('#otp-login-container .input-group, .input-group');
    inputGroups.forEach(group => {
      const desired = {
        display: 'flex',
        'align-items': 'center',
        width: '100%',
        'max-width': '100%',
        'box-sizing': 'border-box'
      };
      Object.keys(desired).forEach(key => {
        if (group.style.getPropertyValue(key) !== desired[key]) {
          group.style.setProperty(key, desired[key], 'important');
        }
      });
    });
  }

  function applyStylesSafely() {
    if (isApplying) return;
    isApplying = true;
    observer.disconnect();
    try {
  injectUltraSpecificCSS();
  forceInlineStyles();
    } finally {
      startObserving();
      isApplying = false;
    }
  }

  function scheduleReapply() {
    if (reapplyScheduled || isApplying) return;
    reapplyScheduled = true;
    requestAnimationFrame(() => {
      reapplyScheduled = false;
      applyStylesSafely();
    });
  }

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'childList') {
        scheduleReapply();
        break;
      }
    }
  });

  function startObserving() {
    const target = document.getElementById('otp-login-container') || document.getElementById('t4s-login-sidebar') || document.body;
    observer.observe(target, {
    childList: true,
      subtree: true
      // Intentionally NOT observing attributes to avoid feedback loops
    });
  }

  // Initial apply
  applyStylesSafely();

  // Single limited re-apply to handle late theme loads
  setTimeout(() => { scheduleReapply(); }, 200);

  // Event listeners
  window.addEventListener('load', scheduleReapply);
  window.addEventListener('resize', () => { requestAnimationFrame(forceInlineStyles); });
  window.addEventListener('DOMContentLoaded', scheduleReapply);
})();